import { useEffect, useState, useRef, memo } from "react";
import { Sparkles, Delete, LogOut, LogIn, Zap, ListChecks, Megaphone, UtensilsCrossed, Check, Palette, Bell, BellRing, Siren, X, PartyPopper, Hand, User, ChevronLeft, ChevronRight, Repeat, Hammer, CalendarDays, Wrench, BookOpen, Pin, Clock, QrCode, Lock, Unlock, Newspaper, Maximize, Loader2, Trophy, ShoppingCart, Plus, Minus, Trash2, Info, Mail, Pencil } from "lucide-react";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { makeT } from "@/lib/i18n";
import { dueMeta, DUE_OPTIONS, pinTimeLabel, SHOP_CATEGORIES, stepQuantity, pindateColor, PINDATE_DAYPARTS, PINDATE_NEUTRAL, localISO, taskEffDate, weekdayDayLabel } from "@/lib/labels";
import { KIOSK_THEMES, getKioskTheme } from "@/lib/kioskThemes";
import NotifPrefsDialog from "@/components/NotifPrefsDialog";
import { Confetti } from "@/components/Confetti";
import { BonusSpinBanner } from "@/components/BonusSpinBanner";
import { QuickTileWizard } from "@/components/QuickTileWizard";

const TILE_PASTELS = ["#FDE2E4", "#E2ECFD", "#E3F6E8", "#FFF1D6", "#EDE4FB", "#DDF4F6", "#FCE7D8", "#F0F4D9", "#F9E1F3", "#E6E9F0"];
import { Tip } from "@/components/Tip";
import { OrgIconContent } from "@/components/OrgIcon";
import KioskMore from "@/components/KioskMore";
import WheelOfFortune from "@/components/WheelOfFortune";
import { toast } from "sonner";

const Avatar = ({ u, size = "w-16 h-16" }) => (
  u?.avatar_url
    ? <img src={u.avatar_url} alt={u.name} className={`${size} rounded-full object-cover brutal-sm`} />
    : <div className={`${size} rounded-full brutal-sm bg-white flex items-center justify-center font-head font-black text-2xl`}>{u?.name?.[0]?.toUpperCase()}</div>
);

const LOCALE = { nl: "nl-NL", en: "en-GB", tr: "tr-TR", ar: "ar" };
const iso = (d) => localISO(d);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const relDate = (isoStr, kt = (s) => s, loc = "nl-NL") => {
  if (!isoStr) return "";
  const d = new Date(isoStr); const t = new Date();
  t.setHours(0, 0, 0, 0); d.setHours(0, 0, 0, 0);
  const diff = Math.round((d - t) / 86400000);
  const dm = d.toLocaleDateString(loc, { day: "numeric", month: "short" });
  if (diff === 0) return kt("Vandaag");
  if (diff === 1) return `${kt("Morgen")} ${dm}`;
  if (diff === 2) return `${kt("Overmorgen")} ${dm}`;
  return d.toLocaleDateString(loc, { weekday: "short", day: "numeric", month: "short" });
};

const MODULE_TILES = [
  { key: "klusjes", label: "Klusjes", icon: Hammer, route: "/klusjes" },
  { key: "ruilmarkt", label: "Ruilmarkt", icon: Repeat, route: "/ruilmarkt" },
  { key: "eten", label: "Eten", icon: UtensilsCrossed, route: "/eten" },
  { key: "rooster", label: "Rooster", icon: CalendarDays, route: "/rooster" },
  { key: "pindates", label: "Pin Dates", icon: Pin, route: "/meer/pindates" },
  { key: "activiteiten", label: "Activiteiten", icon: Sparkles, route: "/meer/activiteiten" },
  { key: "onderhoud", label: "Onderhoud", icon: Wrench, route: "/meer/onderhoud" },
  { key: "kennisbank", label: "Hoedan", icon: BookOpen, route: "/meer/kennisbank" },
];

// Kiosk-indeling: welke blokken in welke kolom + standaardvolgorde
const MAIN_BLOCKS = ["quicktiles", "allowance", "tasks"];
const SIDE_BLOCKS = ["mededelingen", "nieuws", "poll", "cook_tonight", "cook_proposal", "vieringen", "pindates", "boodschappen", "nu_log", "swaps", "modules", "qr"];
const orderBlocks = (saved, def) => {
  const s = Array.isArray(saved) ? saved.filter((id) => def.includes(id)) : [];
  return [...s, ...def.filter((id) => !s.includes(id))];
};
const ALL_BLOCK_IDS = [...MAIN_BLOCKS, ...SIDE_BLOCKS];
const K_HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const K_MINS = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];
function KTimeField({ value, onChange, testid, placeholderHint }) {
  const [h, m] = (value || "").split(":");
  const set = (nh, nm) => { if (!nh && !nm) { onChange(""); return; } onChange(`${nh || "00"}:${nm || "00"}`); };
  const cls = "flex-1 min-w-0 k-pill rounded-xl px-2 py-2.5 font-bold outline-none text-center cursor-pointer";
  return (
    <div className="flex items-center gap-1 min-w-0" data-testid={testid}>
      <select data-testid={`${testid}-h`} value={h || ""} onChange={(e) => set(e.target.value, m || "00")} className={cls}>
        <option value="">{placeholderHint || "uu"}</option>
        {K_HOURS.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
      <span className="font-black opacity-50 shrink-0">:</span>
      <select data-testid={`${testid}-m`} value={m || ""} onChange={(e) => set(h || "00", e.target.value)} className={cls}>
        <option value="">mm</option>
        {K_MINS.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
    </div>
  );
}

const resolveLayout = (layout) => {
  layout = layout || {};
  const sm = Array.isArray(layout.main) ? layout.main.filter((id) => ALL_BLOCK_IDS.includes(id)) : null;
  const ss = Array.isArray(layout.side) ? layout.side.filter((id) => ALL_BLOCK_IDS.includes(id)) : null;
  if (sm === null && ss === null) return { mainCol: [...MAIN_BLOCKS], sideCol: [...SIDE_BLOCKS] };
  const used = new Set([...(sm || []), ...(ss || [])]);
  const extraMain = MAIN_BLOCKS.filter((id) => !used.has(id));
  const extraSide = SIDE_BLOCKS.filter((id) => !used.has(id));
  return { mainCol: [...(sm || []), ...extraMain], sideCol: [...(ss || []), ...extraSide] };
};

// Geïsoleerde PIN-pad: eigen lokale state zodat het intoetsen NIET het hele kioskbord her-rendert (snappy op tablets)
const PinPad = memo(function PinPad({ label, title, theme, kt, busy, onSubmit, onClose }) {
  const [pin, setPin] = useState("");
  const press = (d) => setPin((p) => (p + d).slice(0, 6));
  const confirm = async () => { const ok = await onSubmit(pin); if (ok === false) setPin(""); };
  return (
    <>
      <button data-testid="kiosk-pin-close" onClick={onClose} className="k-pill brutal-press rounded-full p-2.5 absolute top-5 right-5"><X className="w-6 h-6" strokeWidth={2.5} /></button>
      <h3 className="font-head font-black text-2xl text-center mt-2">{label}</h3>
      {title && <p className="k-muted font-semibold -mt-3 text-center">"{title}"</p>}
      <div className="flex gap-3">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className={`w-4 h-4 rounded-full border-2 ${i < pin.length ? "k-accent" : "opacity-30"}`} style={{ borderColor: theme.vars["--k-text"] }} />)}</div>
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <button key={n} data-testid={`kiosk-pin-key-${n}`} onClick={() => press(String(n))} className="k-pill brutal-press h-20 w-20 rounded-full text-3xl font-head font-black active:scale-95 transition-transform">{n}</button>
        ))}
        <button onClick={() => setPin((p) => p.slice(0, -1))} className="k-pill brutal-press h-20 w-20 rounded-full flex items-center justify-center active:scale-95 transition-transform"><Delete className="w-7 h-7" strokeWidth={2.5} /></button>
        <button data-testid="kiosk-pin-key-0" onClick={() => press("0")} className="k-pill brutal-press h-20 w-20 rounded-full text-3xl font-head font-black active:scale-95 transition-transform">0</button>
        <button data-testid="kiosk-pin-confirm" onClick={confirm} disabled={pin.length < 4 || busy} className="k-accent brutal-press h-20 w-20 rounded-full flex items-center justify-center disabled:opacity-30 active:scale-95 transition-transform">{busy ? <Loader2 className="w-8 h-8 animate-spin" strokeWidth={3} /> : <Check className="w-8 h-8" strokeWidth={3} />}</button>
      </div>
    </>
  );
});

const UpdatedPill = ({ ts, pulse, kt }) => {
  if (!ts) return null;
  return (
    <div data-testid="kiosk-updated-pill" title={kt("Automatisch verversen elke 15 sec")} className="k-pill rounded-full p-2.5 hidden md:flex items-center justify-center select-none">
      <span className="relative flex h-2.5 w-2.5">
        {pulse && <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: "#10B981" }} />}
        <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: "#10B981" }} />
      </span>
    </div>
  );
};

const AnnouncementBanner = ({ items, kt, onEvent }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (items.length <= 1) return;
    const iv = setInterval(() => setIdx((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(iv);
  }, [items.length]);
  const a = items[Math.min(idx, items.length - 1)];
  if (!a) return null;
  return (
    <div className="mx-4 mt-4 k-accent rounded-3xl px-6 py-5 animate-pop" data-testid="kiosk-announcement-banner">
      <div className="flex items-start gap-4">
        <Megaphone className="w-9 h-9 shrink-0 mt-0.5" strokeWidth={2.5} />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-black uppercase tracking-wider opacity-70 mb-0.5">{kt("Mededeling")}</p>
          <p className="font-head font-black text-2xl leading-tight break-words" data-testid="kiosk-banner-title">{a.title}</p>
          {a.description && <p className="text-base font-semibold opacity-90 mt-1 break-words">{a.description}</p>}
          {a.event_from && (
            <button data-testid={`kiosk-banner-event-${a.id}`} onClick={() => onEvent?.(a)}
              className="mt-2.5 brutal-press rounded-full px-3 py-1.5 text-sm font-bold inline-flex items-center gap-1.5 bg-white/90 text-brand-ink">
              <CalendarDays className="w-4 h-4" strokeWidth={2.5} />{a.event_from}{a.event_from_time ? ` · ${a.event_from_time}` : ""} — {kt("Zet in agenda")}
            </button>
          )}
        </div>
        {items.length > 1 && (
          <div className="flex flex-col gap-1.5 shrink-0 pt-1" data-testid="kiosk-banner-dots">
            {items.map((_, i) => (
              <span key={i} className="w-2.5 h-2.5 rounded-full transition-opacity" style={{ background: "currentColor", opacity: i === idx ? 1 : 0.35 }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function Kiosk() {
  const [board, setBoard] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [pulse, setPulse] = useState(false);
  const [me, setMe] = useState(null);
  const [myData, setMyData] = useState(null);
  const [themeId, setThemeId] = useState(localStorage.getItem("doenies_kiosk_theme") || "doenies");
  const [themeOpen, setThemeOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [installQrOpen, setInstallQrOpen] = useState(false);
  const [rulesQrOpen, setRulesQrOpen] = useState(false);
  const [rulesView, setRulesView] = useState(false);
  const [rulesPages, setRulesPages] = useState(null);
  const [rulesErr, setRulesErr] = useState(false);
  const [eventQr, setEventQr] = useState(null);
  const [feedQr, setFeedQr] = useState(null);
  const [storyView, setStoryView] = useState(null);
  const [kioskAiResult, setKioskAiResult] = useState(null);
  const [shopAddName, setShopAddName] = useState("");
  const [shopAddQty, setShopAddQty] = useState("");
  const [shopClearN, setShopClearN] = useState(null);
  const [pointDetail, setPointDetail] = useState(null);
  const [shopClearAll, setShopClearAll] = useState(null);
  const kioskOrg = localStorage.getItem("slackr_org") || "";
  const [pinFlow, setPinFlow] = useState(null);
  const [assignChoice, setAssignChoice] = useState(null);
  const [assignBusy, setAssignBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [arOpen, setArOpen] = useState(false);
  const [arAll, setArAll] = useState(true);
  const [arSel, setArSel] = useState([]);
  const [kioskPostpone, setKioskPostpone] = useState(null);
  const [kpDate, setKpDate] = useState("");
  const [kpReason, setKpReason] = useState("");
  const busyRef = useRef(false);
  const [kioskNudge, setKioskNudge] = useState(null);
  const nudgeSeen = useRef(new Set());
  const nudgeAudio = useRef(null);
  const nudgeBeep = useRef(null);
  const [nudgeSending, setNudgeSending] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);
  const kioskNudgeTask = async (task) => {
    if (nudgeSending) return;
    setNudgeSending(true);
    try {
      const { data } = await api.post(`/kiosk/tasks/${task.id}/nudge`);
      const n = data?.recipients ?? 0;
      toast.success(`✓ ${kt("Verstuurd naar")} ${n} ${n === 1 ? kt("persoon") : kt("personen")}`, { duration: 4000 });
      setTaskDetail(null);
    } catch (e) { toast.error(e.response?.data?.detail || kt("Mislukt")); }
    finally { setNudgeSending(false); }
  };
  const startNudgeAlarm = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        nudgeAudio.current = ctx;
        const beep = () => {
          const o = ctx.createOscillator(); const g = ctx.createGain();
          o.type = "square"; o.connect(g); g.connect(ctx.destination);
          g.gain.setValueAtTime(0.0001, ctx.currentTime);
          g.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + 0.02);
          o.frequency.setValueAtTime(880, ctx.currentTime);
          o.frequency.setValueAtTime(660, ctx.currentTime + 0.18);
          g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.36);
          o.start(); o.stop(ctx.currentTime + 0.38);
        };
        beep(); nudgeBeep.current = setInterval(beep, 850);
      }
    } catch { /* geluid geblokkeerd */ }
    try { navigator.vibrate?.([300, 150, 300, 150, 600]); } catch { /* nvt */ }
  };
  const stopNudgeAlarm = () => {
    if (nudgeBeep.current) { clearInterval(nudgeBeep.current); nudgeBeep.current = null; }
    try { nudgeAudio.current?.close?.(); } catch { /* nvt */ }
    nudgeAudio.current = null;
    try { navigator.vibrate?.(0); } catch { /* nvt */ }
  };
  useEffect(() => {
    // Bewust GEEN fullscreen alarm op de Kiosk: een NU!-melding gaat alleen naar de
    // persoon die de taak heeft (hun eigen app via NudgeAlarm.jsx). Het gedeelde
    // Kiosk-bord toont het NU!-logboek wel, maar niet het alarm.
  }, [board]);
  useEffect(() => {
    if (kioskNudge) { startNudgeAlarm(); return stopNudgeAlarm; }
    stopNudgeAlarm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskNudge]);
  const [quickBusy, setQuickBusy] = useState(false);
  const [kioskSpin, setKioskSpin] = useState(null);
  const [dayOffset, setDayOffset] = useState(0);
  // Reset de 'Details'-keuzes telkens wanneer het toewijs-menu opent (default = huidige daglijst).
  const [taskDetail, setTaskDetail] = useState(null);
  const [completeAttr, setCompleteAttr] = useState(null);
  const [takeChoice, setTakeChoice] = useState(null);
  const [takeAssignOpen, setTakeAssignOpen] = useState(false);
  const [takeAssignee, setTakeAssignee] = useState("");
  const [takeNote, setTakeNote] = useState("");
  const [assignFlash, setAssignFlash] = useState(null);
  const [attrDoer, setAttrDoer] = useState("");
  const [attrPickOpen, setAttrPickOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [calView, setCalView] = useState("week");
  const [calCursor, setCalCursor] = useState(new Date());
  const [calPinDetail, setCalPinDetail] = useState(null);
  const [calAdd, setCalAdd] = useState(null);
  const [pollVoters, setPollVoters] = useState(null);
  const [cookServings, setCookServings] = useState(null);
  useEffect(() => { setCookServings(null); }, [board?.cook_proposal?.poll_id]);
  useEffect(() => {
    if (!rulesView || rulesPages || !kioskOrg) return;
    setRulesErr(false);
    api.get(`/public/spelregels/${kioskOrg}/pages`)
      .then((r) => setRulesPages(r.data.pages || []))
      .catch(() => setRulesErr(true));
  }, [rulesView, rulesPages, kioskOrg]);
  const [moreKey, setMoreKey] = useState(null);
  const [moreReload, setMoreReload] = useState(0);
  const [idle, setIdle] = useState(false);
  const [burstKey, setBurstKey] = useState(null);
  const [verified, setVerified] = useState(() => localStorage.getItem("doenies_kiosk_verified") === "1");
  const [codeInput, setCodeInput] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);
  const [locked, setLocked] = useState(() => localStorage.getItem("doenies_kiosk_locked") === "1");
  const [lockPinConfigured, setLockPinConfigured] = useState(false);
  const [isFs, setIsFs] = useState(() => (typeof window !== "undefined" && (window.navigator.standalone === true || window.matchMedia("(display-mode: standalone)").matches)) || (typeof document !== "undefined" && !!document.fullscreenElement));
  const [fsPromptDismissed, setFsPromptDismissed] = useState(false);
  const lastActive = useRef(Date.now());
  const prevEarnedRef = useRef(null);
  // Confetti wanneer iemand deze week nieuw zijn zakgeld-drempel haalt
  useEffect(() => {
    const members = board?.allowance?.members || [];
    const earned = new Set(members.filter((m) => m.earned).map((m) => m.name));
    if (prevEarnedRef.current !== null) {
      for (const nm of earned) {
        if (!prevEarnedRef.current.has(nm)) { setBurstKey(Date.now()); break; }
      }
    }
    prevEarnedRef.current = earned;
  }, [board]);

  const kt = makeT(board?.kiosk_language || "nl");
  const loc = LOCALE[board?.kiosk_language || "nl"] || "nl-NL";

  const verifyKiosk = async () => {
    setCodeBusy(true);
    try {
      const { data } = await api.post("/kiosk/verify-code", { code: codeInput });
      if (data.ok) {
        localStorage.setItem("doenies_kiosk_verified", "1");
        localStorage.setItem("doenies_kiosk_code", codeInput); setVerified(true);
        // Maak de activatie 'sticky': bewaar code (+org) in de URL, zodat een reload na een
        // redeploy of storage-wipe automatisch heractiveert en de kiosk nooit uitlogt.
        try {
          const org = localStorage.getItem("slackr_org") || new URLSearchParams(window.location.search).get("org") || "";
          const u = new URL(window.location.href);
          u.searchParams.set("code", codeInput);
          if (org) u.searchParams.set("org", org);
          window.history.replaceState({}, "", u.toString());
        } catch { /* nvt */ }
      }
      else toast.error(kt("Onjuiste code"));
    } catch { toast.error(kt("Verificatie mislukt")); }
    finally { setCodeBusy(false); }
  };

  const enterFullscreen = () => {
    // In standalone/PWA (op het beginscherm) is de kiosk al volledig scherm; de Fullscreen-API
    // aanroepen zou alleen de browsermelding "druk op Esc om het volledige scherm te sluiten" tonen.
    // Daarom in standalone-modus overslaan.
    const standalone = typeof window !== "undefined" && (window.navigator.standalone === true || window.matchMedia("(display-mode: standalone)").matches);
    if (standalone || board?.kiosk_no_fullscreen) { setIsFs(true); setFsPromptDismissed(true); return; }
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen().catch(() => setFsPromptDismissed(true));
    else setFsPromptDismissed(true);
  };
  const lockKiosk = () => { setLocked(true); localStorage.setItem("doenies_kiosk_locked", "1"); setFsPromptDismissed(false); enterFullscreen(); };
  const unlockKiosk = () => {
    setLocked(false); localStorage.removeItem("doenies_kiosk_locked"); setFsPromptDismissed(true);
    if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {});
  };
  // eerste tik zet kiosk in fullscreen (browsers vereisen een gebruikersactie)
  const startKiosk = () => { if (locked) enterFullscreen(); else lockKiosk(); };

  // Na een refresh gaat fullscreen verloren (browserbeleid). Als de kiosk vergrendeld
  // was, herstel fullscreen dan automatisch bij de eerstvolgende aanraking/toets —
  // zonder dat er eerst een knop getikt hoeft te worden.
  useEffect(() => {
    if (!(verified && board && locked && !isFs)) return;
    const go = () => { enterFullscreen(); cleanup(); };
    const cleanup = () => {
      window.removeEventListener("pointerdown", go);
      window.removeEventListener("keydown", go);
      window.removeEventListener("touchstart", go);
    };
    window.addEventListener("pointerdown", go);
    window.addEventListener("keydown", go);
    window.addEventListener("touchstart", go);
    return cleanup;
  }, [verified, board, locked, isFs]);

  // fetch of er een vergrendel-PIN is ingesteld (informatief) + zelfherstellende activatie
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const org = params.get("org");
    if (org) localStorage.setItem("slackr_org", org);
    // Zelfherstellend: als de URL een kiosk-code bevat, heractiveer automatisch.
    // Zo blijft een gebookmarkte kiosk-link werken, óók als de tablet z'n opslag wist
    // (na een update/redeploy of idle) — de kiosk logt zichzelf dan nooit meer uit.
    // Zelfherstellend: gebruik de code uit de URL óf een eerder opgeslagen code, en
    // her-verifieer stil zodra de 'verified'-vlag ontbreekt (bijv. na storage-eviction op tablets).
    // Zo hoeft de kiosk nooit meer handmatig opnieuw via code aangemeld te worden.
    const storedCode = localStorage.getItem("doenies_kiosk_code");
    const urlCode = params.get("code") || params.get("kioskcode") || storedCode;
    if (urlCode && localStorage.getItem("doenies_kiosk_verified") !== "1") {
      api.post("/kiosk/verify-code", { code: urlCode })
        .then(({ data }) => {
          if (data.ok) {
            localStorage.setItem("doenies_kiosk_verified", "1");
            localStorage.setItem("doenies_kiosk_code", urlCode);
            setVerified(true);
            try {
              const u = new URL(window.location.href);
              u.searchParams.set("code", urlCode);
              const o = localStorage.getItem("slackr_org");
              if (o) u.searchParams.set("org", o);
              window.history.replaceState({}, "", u.toString());
            } catch { /* nvt */ }
          }
        })
        .catch(() => {});
    }
    api.get("/kiosk/lock-status").then((r) => setLockPinConfigured(r.data.pin_configured)).catch(() => {});
  }, []);

  // houd fullscreen-status bij
  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const theme = getKioskTheme(themeId);
  const applyTheme = (id) => { setThemeId(id); localStorage.setItem("doenies_kiosk_theme", id); setThemeOpen(false); };

  // Per-widget kleuren (alleen in het Slackr-thema); anders het thema-accent
  const WIDGET_COLORS = {
    quicktiles: { a: "#FBBF24", ink: "#3B2A00" },
    tasks: { a: "#10B981", ink: "#04352A" },
    poll: { a: "#F472B6", ink: "#4A1030" },
    vieringen: { a: "#C84BE0", ink: "#FFFFFF" },
    swaps: { a: "#34D399", ink: "#053B2E" },
    boodschappen: { a: "#F9A8D4", ink: "#4A1030" },
    meldingen: { a: "#8B5CFF", ink: "#FFFFFF" },
    modules: { a: "#7C3AED", ink: "#FFFFFF" },
    qr: { a: "#2563EB", ink: "#FFFFFF" },
  };
  const wStyle = (key) => (themeId === "doenies" && WIDGET_COLORS[key]) ? { "--k-accent": WIDGET_COLORS[key].a, "--k-accent-ink": WIDGET_COLORS[key].ink } : undefined;

  const loadBoard = async () => {
    setBoard((await api.get("/kiosk/board")).data);
    setLastUpdated(Date.now());
    setPulse(true);
    setTimeout(() => setPulse(false), 1600);
  };
  const loadMy = async () => setMyData((await api.get("/dashboard")).data);
  const openPointDetail = async (m) => {
    if (!m?.user_id) return;
    setPointDetail({ loading: true, member: m });
    try { const { data } = await api.get(`/kiosk/rewards/breakdown/${m.user_id}`); setPointDetail({ member: m, ...data }); }
    catch (e) { setPointDetail({ member: m, events: [], week_total: m.points }); }
  };
  useEffect(() => { loadBoard(); const t = setInterval(loadBoard, 15000); return () => clearInterval(t); }, []);
  // ververs persoonlijke taken automatisch in persoonlijke modus
  useEffect(() => {
    if (!me) return;
    const t = setInterval(loadMy, 15000);
    return () => clearInterval(t);
  }, [me]);

  // idle detection (2 min) -> terug naar startscherm
  useEffect(() => {
    const bump = () => { lastActive.current = Date.now(); setIdle((v) => (v ? false : v)); };
    const evts = ["pointerdown", "touchstart", "keydown", "mousemove"];
    evts.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const iv = setInterval(() => { if (Date.now() - lastActive.current > 120000) setIdle(true); }, 5000);
    return () => { clearInterval(iv); evts.forEach((e) => window.removeEventListener(e, bump)); };
  }, []);
  const exitPersonal = () => { localStorage.removeItem("doenies_token"); setMe(null); setMyData(null); };
  useEffect(() => {
    if (idle) { setPinFlow(null); setKioskSpin(null); setTaskDetail(null); setCompleteAttr(null); setTakeChoice(null); setTakeAssignOpen(false); setTakeAssignee(""); setTakeNote(""); setAttrPickOpen(false); setAttrDoer(""); setMoreKey(null); setThemeOpen(false); setPrefsOpen(false); setDayOffset(0); setStoryView(null); setEventQr(null); setFeedQr(null); setRulesQrOpen(false); setRulesView(false); exitPersonal(); }
  }, [idle]);
  // jarige-confetti elke 5 min, alleen in idle-stand
  useEffect(() => {
    if (!idle || !(board?.birthdays_today?.length)) return;
    setBurstKey(Date.now());
    const iv = setInterval(() => setBurstKey(Date.now()), 300000);
    return () => clearInterval(iv);
  }, [idle, board?.birthdays_today]);

  const startFlow = (flow) => { setPinFlow(flow); };
  const closeFlow = () => { setPinFlow(null); };
  const handleQuickTile = async (t) => {
    if (!me) { startFlow({ action: "instantiate", templateId: t.id, title: t.title, groupIds: t.group_ids || [] }); return; }
    if (busyRef.current) return;
    busyRef.current = true;
    setQuickBusy(true);
    try {
      await api.post(`/tasks/${t.id}/instantiate`, { due_when: "vandaag", due_date: selISO, assignee_ids: [me.user_id] });
      toast.success(dayOffset === 0 ? kt("Ingepland") : `${kt("Ingepland voor")} ${dayLabel}`);
      loadMy();
    } catch (e) { toast.error(e.response?.data?.detail || kt("Kon niet inplannen")); }
    finally { busyRef.current = false; setQuickBusy(false); }
  };
  const runInstantiate = async (payload, label) => {
    if (!assignChoice || busyRef.current) return;
    busyRef.current = true;
    setAssignBusy(true);
    try {
      await api.post(`/kiosk/quick-tiles/${assignChoice.templateId}/instantiate`, { pin: assignChoice.pin, ...payload });
      loadBoard();
      toast.success(`${kt("Ingepland")} — ${label}`);
      setAssignChoice(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || kt("Kon niet inplannen"));
      if (e.response?.status === 401) setAssignChoice(null);
    }
    finally { busyRef.current = false; setAssignBusy(false); }
  };
  const wizardUsers = (board?.all_members || board?.users || []).filter((m) => {
    if (m.role === "super_admin" || m.is_super_admin) return false;
    const g = assignChoice?.groupIds || [];
    return !g.length || (m.group_ids || []).some((x) => g.includes(x));
  });
  const [pinBusy, setPinBusy] = useState(false);
  const [pinSuccess, setPinSuccess] = useState(null);

  const submitPin = async (p) => {
    if (pinBusy) return false;
    let successMsg = null;
    setPinBusy(true);
    try {
      if (pinFlow.action === "unlock") {
        const { data } = await api.post("/kiosk/unlock", { pin: p });
        if (data.ok) { unlockKiosk(); toast.success(kt("Kioskmodus ontgrendeld 🔓")); closeFlow(); return true; }
        toast.error(kt("Onjuiste pincode")); return false;
      }
      if (pinFlow.action === "login" || pinFlow.action === "module") {
        const r = await api.post("/kiosk/resolve-pin", { pin: p });
        const { data } = await api.post("/auth/pin-login", { user_id: r.data.user_id, pin: p });
        localStorage.setItem("doenies_token", data.session_token);
        localStorage.setItem("doenies_kiosk_session", "1");
        window.location.href = pinFlow.route || "/vandaag";
        return true;
      } else if (pinFlow.action === "subscribe") {
        const { data } = await api.post("/kiosk/agenda/feed-url", { pin: p });
        setFeedQr(data); closeFlow();
        return true;
      } else if (pinFlow.action === "complete") {
        const { data } = await api.post(`/kiosk/tasks/${pinFlow.taskId}/complete`, { pin: p });
        if (data?.needs_attribution) {
          setCompleteAttr({ taskId: pinFlow.taskId, pin: p, title: pinFlow.title, assignees: data.assignees || [], isUnassigned: data.is_unassigned });
          setAttrPickOpen(false); setAttrDoer(""); closeFlow(); return true;
        }
        loadBoard(); setBurstKey(Date.now()); successMsg = kt("Afgevinkt");
      } else if (pinFlow.action === "take") {
        const { data } = await api.post(`/kiosk/tasks/${pinFlow.taskId}/take`, { pin: p });
        if (data?.needs_choice) { setTakeChoice({ taskId: pinFlow.taskId, pin: p, title: pinFlow.title }); setTakeAssignOpen(false); setTakeAssignee(""); closeFlow(); return true; }
        loadBoard(); successMsg = kt("Taak overgenomen");
      } else if (pinFlow.action === "action_reminder") {
        await api.post("/kiosk/notifications/action-reminder", { pin: p, user_ids: pinFlow.user_ids || [] }); successMsg = kt("Melding verstuurd 📣");
      } else if (pinFlow.action === "delete") {
        await api.post(`/kiosk/tasks/${pinFlow.taskId}/delete`, { pin: p, revert_penalty: !!pinFlow.revert_penalty }); loadBoard(); successMsg = kt("Taak verwijderd");
      } else if (pinFlow.action === "postpone") {
        await api.post(`/kiosk/tasks/${pinFlow.taskId}/postpone`, { pin: p, new_date: pinFlow.new_date, reason: pinFlow.reason }); loadBoard(); successMsg = kt("Taak uitgesteld 📆");
      } else if (pinFlow.action === "add_pindate") {
        await api.post("/kiosk/pindates", { pin: p, event_date: pinFlow.event_date, title: pinFlow.title, event_time: pinFlow.event_time, event_end_time: pinFlow.event_end_time, event_daypart: pinFlow.event_daypart, description: pinFlow.description }); loadBoard(); successMsg = kt("Pin date toegevoegd 📌");
      } else if (pinFlow.action === "edit_pindate") {
        await api.put(`/kiosk/pindates/${pinFlow.pinId}`, { pin: p, event_date: pinFlow.event_date, title: pinFlow.title, event_time: pinFlow.event_time, event_end_time: pinFlow.event_end_time, event_daypart: pinFlow.event_daypart, description: pinFlow.description }); loadBoard(); successMsg = kt("Pin date bijgewerkt 📌");
      } else if (pinFlow.action === "delete_pindate") {
        await api.post(`/kiosk/pindates/${pinFlow.pinId}/delete`, { pin: p }); loadBoard(); successMsg = kt("Pin date verwijderd");
      } else if (pinFlow.action === "shop_toggle") {
        await api.post(`/kiosk/shopping/${pinFlow.itemId}/toggle`, { pin: p }); loadBoard(); successMsg = kt("Bijgewerkt");
      } else if (pinFlow.action === "shop_qty") {
        await api.post(`/kiosk/shopping/${pinFlow.itemId}/quantity`, { pin: p, quantity: pinFlow.quantity }); loadBoard(); successMsg = kt("Bijgewerkt");
      } else if (pinFlow.action === "shop_clear") {
        await api.post("/kiosk/shopping/clear-checked", { pin: p }); loadBoard(); successMsg = kt("Afgevinkte gewist");
      } else if (pinFlow.action === "shop_clear_all") {
        await api.post("/kiosk/shopping/clear-all", { pin: p }); loadBoard(); successMsg = kt("Lijst geleegd");
      } else if (pinFlow.action === "shop_add") {
        await api.post("/kiosk/shopping", { pin: p, name: pinFlow.name, quantity: pinFlow.quantity || "" }); loadBoard(); successMsg = kt("Toegevoegd");
      } else if (pinFlow.action === "shop_delete") {
        await api.post(`/kiosk/shopping/${pinFlow.itemId}/delete`, { pin: p }); loadBoard(); successMsg = kt("Verwijderd");
      } else if (pinFlow.action === "takeSwap") {
        await api.post(`/kiosk/swaps/${pinFlow.swapId}/take`, { pin: p }); loadBoard(); successMsg = kt("Top! Jij doet dit");
      } else if (pinFlow.action === "vote") {
        await api.post(`/kiosk/polls/${pinFlow.pollId}/vote`, { pin: p, option: pinFlow.option }); loadBoard(); successMsg = kt("Stem opgeslagen");
      } else if (pinFlow.action === "planCook") {
        await api.post(`/kiosk/polls/${pinFlow.pollId}/plan-cook`, { pin: p, servings: pinFlow.servings }); loadBoard(); successMsg = kt("Kooktaak ingepland");
      } else if (pinFlow.action === "instantiate") {
        await api.post("/kiosk/resolve-pin", { pin: p });
        setAssignChoice({ templateId: pinFlow.templateId, pin: p, groupIds: pinFlow.groupIds || [], title: pinFlow.title }); closeFlow(); return true;
      } else if (pinFlow.action === "participate") {
        const { data } = await api.post(`/kiosk/content/${pinFlow.itemId}/participate`, { pin: p });
        setMoreReload((k) => k + 1); successMsg = data.joined ? kt("Je doet mee") : kt("Je bent afgemeld");
      } else if (pinFlow.action === "klusTake") {
        await api.post(`/kiosk/klusjes/${pinFlow.klusId}/take`, { pin: p }); setMoreReload((k) => k + 1); successMsg = kt("Je pakt dit klusje op");
      } else if (pinFlow.action === "spin") {
        const { data } = await api.post("/kiosk/spin/check", { pin: p });
        if (!data.prizes || data.prizes.length < 2) { toast.error(kt("Het Rad van Fortuin is nog niet ingesteld")); return false; }
        if (!data.credits) { toast.error(`${kt("Helaas")}, ${data.name}: ${kt("je hebt geen spins te goed")}`); return false; }
        setKioskSpin({ pin: p, name: data.name, credits: data.credits, prizes: data.prizes });
        closeFlow();
        return true;
      }
      if (successMsg) {
        setPinSuccess(successMsg);
        setTimeout(() => { closeFlow(); setTaskDetail(null); setPinSuccess(null); }, 1100);
      } else { closeFlow(); setTaskDetail(null); }
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || kt("Onjuiste pincode")); return false; }
    finally { setPinBusy(false); }
  };

  const submitAttr = async (payload) => {
    const a = completeAttr; if (!a) return;
    try {
      await api.post(`/kiosk/tasks/${a.taskId}/complete`, { pin: a.pin, confirm_attribution: true, ...payload });
      loadBoard(); setBurstKey(Date.now());
      setCompleteAttr(null); setAttrDoer(""); setAttrPickOpen(false); setTaskDetail(null);
      toast.success(kt("Afgevinkt"));
    } catch (e) { toast.error(e.response?.data?.detail || kt("Mislukt")); }
  };

  const submitTake = async (payload) => {
    const a = takeChoice; if (!a) return;
    try {
      await api.post(`/kiosk/tasks/${a.taskId}/take`, { pin: a.pin, ...payload });
      loadBoard();
      setTakeChoice(null); setTakeAssignOpen(false); setTakeAssignee(""); setTakeNote(""); setTaskDetail(null);
      if (payload.assign_to) {
        const nm = (board?.users || []).find((u) => u.user_id === payload.assign_to)?.name || kt("iemand");
        setAssignFlash(`📣 ${kt("Toegewezen aan")} ${nm} — ${kt("SMS verstuurd")}`);
        setTimeout(() => setAssignFlash(null), 6000);
      } else {
        toast.success(kt("Taak overgenomen"));
      }
    } catch (e) { toast.error(e.response?.data?.detail || kt("Mislukt")); }
  };

  const doShopDirect = async ({ action, itemId, quantity, name }) => {
    try {
      if (action === "shop_toggle") await api.post(`/kiosk/shopping/${itemId}/toggle`, {});
      else if (action === "shop_qty") await api.post(`/kiosk/shopping/${itemId}/quantity`, { quantity });
      else if (action === "shop_delete") await api.post(`/kiosk/shopping/${itemId}/delete`, {});
      else if (action === "shop_add") await api.post("/kiosk/shopping", { name, quantity: quantity || "" });
      else if (action === "shop_clear") await api.post("/kiosk/shopping/clear-checked", {});
      else if (action === "shop_clear_all") await api.post("/kiosk/shopping/clear-all", {});
      loadBoard();
    } catch (e) { toast.error(e.response?.data?.detail || kt("Mislukt")); }
  };
  const shopFlow = (action, extra = {}) => {
    if (board.shopping_kiosk_no_pin) doShopDirect({ action, ...extra });
    else startFlow({ action, ...extra });
  };

  const aiAssign = async (taskId) => {
    try { const { data } = await api.post(`/kiosk/tasks/${taskId}/ai-assign`); loadBoard(); setTaskDetail(null); setKioskAiResult({ name: data.name, reason: data.reason }); }
    catch (e) { toast.error(e.response?.data?.detail || kt("Toewijzen mislukt")); }
  };

  const pinLabel = { login: kt("Log in met je pincode"), module: kt("Voer je pincode in"), complete: kt("Taak afvinken"), postpone: kt("Taak uitstellen"), take: kt("Taak overnemen"), takeSwap: kt("Ja, ik doe dit wel!"), vote: kt("Bevestig je stem"), instantiate: kt("Taak inplannen"), participate: kt("Meld je aan met je pincode"), klusTake: kt("Klusje oppakken"), unlock: kt("Kioskmodus verlaten"), subscribe: kt("Voer je pincode in"), planCook: kt("Kooktaak inplannen"), spin: kt("Voer je pincode in om te draaien"), add_pindate: kt("Pin date toevoegen"), edit_pindate: kt("Pin date wijzigen"), delete_pindate: kt("Pin date verwijderen") }[pinFlow?.action];

  const pinOverlay = pinFlow && (
    <div className="fixed inset-0 z-[150] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="kiosk-pin-overlay">
      <div className="k-card rounded-[2rem] p-8 w-full max-w-md relative flex flex-col gap-5 items-center" style={theme.vars}>
        {pinSuccess ? (
          <div className="flex flex-col items-center gap-4 py-8" data-testid="kiosk-pin-success">
            <div className="w-24 h-24 rounded-full flex items-center justify-center animate-pop" style={{ background: "#10B981" }}>
              <Check className="w-14 h-14 text-white animate-pop" strokeWidth={4} />
            </div>
            <p className="font-head font-black text-2xl text-center">{pinSuccess} ✅</p>
          </div>
        ) : (
          <PinPad key={pinFlow.action + (pinFlow.taskId || pinFlow.pollId || pinFlow.templateId || pinFlow.swapId || pinFlow.itemId || pinFlow.klusId || "")}
            label={pinLabel} title={pinFlow.title} theme={theme} kt={kt} busy={pinBusy} onSubmit={submitPin} onClose={closeFlow} />
        )}
      </div>
    </div>
  );

  // Fullscreen start/hervat-overlay: verschijnt bij openen en na ESC (browsers vereisen een tik voor fullscreen)
  const standaloneKiosk = typeof window !== "undefined" && (window.navigator.standalone === true || window.matchMedia("(display-mode: standalone)").matches);
  const showFsPrompt = verified && board && !isFs && !fsPromptDismissed && !locked && !standaloneKiosk && !board?.kiosk_no_fullscreen;
  const fsOverlay = showFsPrompt && (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-xl flex items-center justify-center p-6" data-testid="kiosk-fs-overlay">
      <div className="k-card rounded-[2rem] p-10 w-full max-w-md text-center flex flex-col items-center gap-4" style={theme.vars}>
        <div className="k-accent rounded-3xl w-20 h-20 flex items-center justify-center"><Maximize className="w-10 h-10" strokeWidth={2.5} /></div>
        <h3 className="font-head font-black text-3xl leading-tight">{kt("Kioskmodus starten")}</h3>
        <p className="k-muted font-semibold">{locked
          ? kt("Tik om terug te keren naar volledig scherm. De kiosk blijft vergrendeld tot de pincode wordt ingevoerd.")
          : kt("Tik om Slackr op volledig scherm te vergrendelen. Ontgrendelen kan alleen met de pincode.")}</p>
        <button data-testid="kiosk-fs-start-btn" onClick={startKiosk}
          className="w-full k-accent brutal-press rounded-full px-6 py-4 font-head font-black text-lg flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <Lock className="w-6 h-6" strokeWidth={2.5} />{kt("Volledig scherm")}
        </button>
        {!locked && (
          <button data-testid="kiosk-fs-dismiss-btn" onClick={() => setFsPromptDismissed(true)}
            className="k-muted font-head font-bold text-sm underline underline-offset-4">{kt("Nu niet")}</button>
        )}
      </div>
    </div>
  );

  if (!verified) return (
    <div className="min-h-screen navy-gradient flex items-center justify-center p-6">
      <div className="brutal bg-white rounded-3xl p-8 max-w-sm w-full text-center" data-testid="kiosk-code-gate">
        <div className="azure-gradient w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"><Sparkles className="w-7 h-7 text-white" strokeWidth={3} /></div>
        <h1 className="font-head font-black text-2xl">{kt("Kiosk activeren")}</h1>
        <p className="text-sm text-brand-ink/60 mt-2">{kt("Voer de verificatiecode van je beheerder in. Dit wordt maar één keer gevraagd op dit apparaat.")}</p>
        <input data-testid="kiosk-code-input" value={codeInput} onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === "Enter" && verifyKiosk()} placeholder={kt("Code")} maxLength={8}
          className="w-full brutal-sm rounded-xl px-4 py-3 mt-4 font-head font-black text-2xl tracking-[0.3em] text-center outline-none uppercase" />
        <button data-testid="kiosk-code-submit" onClick={verifyKiosk} disabled={codeBusy || codeInput.length < 4}
          className="w-full ui-btn azure-gradient text-white font-head font-bold py-3 mt-4 brutal-press disabled:opacity-50">{kt("Activeren")}</button>
      </div>
    </div>
  );

  if (!board) return <div className="min-h-screen navy-gradient flex items-center justify-center"><p className="font-head font-black text-2xl text-white animate-pulse">{kt("Laden…")}</p></div>;

  const on = (k) => board.modules_on?.[k] !== false;
  const moreTiles = MODULE_TILES.filter((m) => on(m.key) && (!Array.isArray(board.kiosk_more_modules) || board.kiosk_more_modules.includes(m.key)));
  const moreLabel = (k) => (MODULE_TILES.find((m) => m.key === k) || {}).label || k;
  const allTasks = me ? (myData?.my_tasks || []) : board.open_tasks;
  const quickTiles = me ? (myData?.quick_tiles || []) : board.quick_tiles;
  const today = new Date();
  const selDate = addDays(today, dayOffset);
  const selISO = iso(selDate);
  const taskDate = (t) => taskEffDate(t) || iso(today);
  const todayISO = iso(today);
  const isOverdue = (t) => taskDate(t) < todayISO;
  // Op "Vandaag" tonen we ook verlopen (niet-afgeronde) taken van eerder — die blijven DUE tot ze af zijn.
  const dayTasks = allTasks.filter((t) => taskDate(t) === selISO || (dayOffset === 0 && isOverdue(t)));
  const dayLabel = dayOffset === 0 ? kt("Vandaag") : dayOffset === 1 ? kt("Morgen") : dayOffset === -1 ? kt("Gisteren")
    : selDate.toLocaleDateString(loc, { weekday: "long", day: "numeric", month: "short" });
  const swapWhen = (s) => {
    if (s.task_next_date) return relDate(s.task_next_date, kt, loc);
    if (s.task_due_when === "specifiek" && s.task_due_time) return s.task_due_time;
    return s.task_due_when ? dueMeta(s.task_due_when).label : "—";
  };
  const _annToday = new Date().toISOString().slice(0, 10);
  const _annActive = (a) => a.pinned && (!a.pinned_until || a.pinned_until >= _annToday);
  const pinnedAnns = (board.announcements || []).filter(_annActive);
  const restAnns = (board.announcements || []).filter((a) => !_annActive(a));
  const _layout = board.kiosk_layout || {};
  const { mainCol, sideCol } = resolveLayout(board.kiosk_layout);
  const _hidden = new Set(Array.isArray(_layout.hidden) ? _layout.hidden : []);
  const renderBlock = (id) => {
    if (_hidden.has(id)) return null;
    switch (id) {
      case "quicktiles":
        return quickTiles?.length > 0 ? (
          <div key="quicktiles" className="k-card rounded-3xl p-4" style={wStyle("quicktiles")}>
            <h2 className="font-head text-xl font-black mb-2 flex items-center gap-2"><Zap className="w-6 h-6" strokeWidth={3} style={{ color: "var(--k-accent)" }} />Quick Tiles</h2>
            <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-2">
              {quickTiles.map((t, ti) => (
                <button key={t.id} data-testid={`kiosk-quick-${t.id}`} disabled={quickBusy}
                  onClick={() => handleQuickTile(t)}
                  style={{ background: t.tile_color || TILE_PASTELS[ti % TILE_PASTELS.length], color: "#1F1A2E" }}
                  className="brutal-press rounded-xl p-2 min-h-[60px] flex flex-col items-center justify-center text-center active:scale-[0.97] transition-transform overflow-hidden disabled:opacity-50">
                  <Zap className="w-4 h-4 mb-1 shrink-0 opacity-70" strokeWidth={3} />
                  <p className="font-head font-bold text-xs leading-tight break-words line-clamp-2 w-full">{t.title}</p>
                </button>
              ))}
            </div>
          </div>
        ) : null;
      case "allowance":
        return (board.kiosk_show_allowance !== false && board.allowance && board.allowance.members.length > 0) ? (
          <div key="allowance" className="k-card rounded-3xl p-4" data-testid="kiosk-allowance-card">
            <h2 className="font-head text-xl font-black mb-1 flex items-center gap-2"><Trophy className="w-6 h-6" strokeWidth={3} style={{ color: "var(--k-accent)" }} />{board.allowance.title} — {kt("deze week")}</h2>
            <p className="text-sm k-muted font-semibold mb-3">
              {kt("Haal")} {board.allowance.threshold} {kt("punten deze week")}{board.allowance.reward ? ` ${kt("voor")} ${board.allowance.reward}` : ""}.
            </p>
            <div className="space-y-2">
              {board.allowance.members.map((m, i) => {
                const thr = board.allowance.threshold;
                const wpct = m.earned ? 100 : (thr > 0 ? Math.min(99, Math.floor((m.points / thr) * 100)) : 0);
                const mThr = m.month_threshold != null ? m.month_threshold : (board.allowance.month_threshold || 0);
                const monthOn = mThr > 0 && m.month_points != null;
                const mpct = monthOn ? (m.month_earned ? 100 : Math.min(99, Math.floor((m.month_points / mThr) * 100))) : null;
                return (
                  <div key={i} data-testid={`kiosk-allowance-member-${i}`} onClick={() => openPointDetail(m)} className="k-task rounded-2xl px-3 py-2.5 cursor-pointer brutal-press active:scale-[0.99] transition-transform">
                    <div className="flex items-center gap-2.5">
                      {m.avatar_url
                        ? <img src={m.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                        : <span className="w-8 h-8 rounded-full grid place-items-center font-head font-black text-xs shrink-0" style={{ background: "var(--k-accent)", color: "var(--k-accent-ink)" }}>{(m.name || "?").slice(0, 1).toUpperCase()}</span>}
                      <p className="font-head font-bold text-sm truncate flex-1 min-w-0">{m.name}</p>
                      {m.earned
                        ? <span className="inline-flex items-center gap-1 text-xs font-black text-green-600 shrink-0"><Check className="w-3.5 h-3.5" strokeWidth={3} />{kt("Gehaald!")}</span>
                        : <span className="text-xs font-bold k-muted shrink-0">{kt("nog")} <span style={{ color: "var(--k-accent)" }}>{m.needed}</span></span>}
                      <ChevronRight className="w-4 h-4 k-muted shrink-0" strokeWidth={2.5} />
                    </div>
                    <div className="mt-1.5 flex items-center gap-2">
                      <span className="text-[10px] font-black k-muted w-10 shrink-0">{kt("Week")}</span>
                      <div className="flex-1 h-2 rounded-full bg-black/10 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${wpct}%`, background: m.earned ? "#22C55E" : "var(--k-accent)" }} />
                      </div>
                      <span className="text-[11px] font-black w-9 text-right tabular-nums">{wpct}%</span>
                    </div>
                    {monthOn && (
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-[10px] font-black k-muted w-10 shrink-0">{kt("Maand")}</span>
                        <div className="flex-1 h-2 rounded-full bg-black/10 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${mpct}%`, background: m.month_earned ? "#22C55E" : "#F59E0B" }} />
                        </div>
                        <span className="text-[11px] font-black w-9 text-right tabular-nums">{mpct}%</span>
                      </div>
                    )}
                    {m.bonus_target != null && (
                      <p data-testid={`kiosk-bonus-spin-${i}`} className="mt-1 text-[11px] font-black" style={{ color: m.bonus_earned ? "#16A34A" : "var(--k-accent)" }}>
                        🎡 {m.bonus_earned ? kt("Extra spin verdiend!") : `${kt("Extra spin: nog")} ${m.bonus_needed} ${kt("pt")}`}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null;
      case "tasks":
        return (
          <div key="tasks" className="k-card rounded-3xl p-4" style={wStyle("tasks")}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-head text-xl font-black flex items-center gap-2"><ListChecks className="w-6 h-6" strokeWidth={3} style={{ color: "var(--k-accent)" }} />{me ? kt("Mijn taken") : kt("Openstaande taken")}</h2>
              <div className="flex items-center gap-2">
                <button data-testid="kiosk-day-prev" onClick={() => setDayOffset((d) => Math.max(-30, d - 1))} className="k-pill brutal-press rounded-full p-2"><ChevronLeft className="w-5 h-5" strokeWidth={2.5} /></button>
                <span data-testid="kiosk-day-label" className="font-head font-bold min-w-[110px] text-center capitalize">{dayLabel}</span>
                <button data-testid="kiosk-day-next" onClick={() => setDayOffset((d) => Math.min(30, d + 1))} className="k-pill brutal-press rounded-full p-2"><ChevronRight className="w-5 h-5" strokeWidth={2.5} /></button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {dayTasks.length === 0 && <p className="k-muted font-head font-bold col-span-full py-4">{kt("Niks te doen op deze dag 🎉")}</p>}
              {dayTasks.map((t) => {
                const meta = dueMeta(t.due_when); const Icon = meta.icon;
                return (
                  <button key={t.id} data-testid={`kiosk-task-${t.id}`} onClick={() => setTaskDetail(t)}
                    style={t.postponed_at ? { background: "rgba(245, 200, 66, 0.18)" } : undefined}
                    className="k-task brutal-press rounded-2xl p-3.5 text-left min-h-[92px] active:scale-[0.98] transition-transform">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold font-head" style={{ background: "var(--k-accent)", color: "var(--k-accent-ink)" }}><Icon className="w-3.5 h-3.5" strokeWidth={3} />{t.postponed_at && (t.original_date || t.next_date) ? new Date((t.original_date || t.next_date) + "T00:00:00").toLocaleDateString(loc, { weekday: "short", day: "numeric", month: "short" }) : (t.due_when === "specifiek" ? t.due_time : kt(meta.label))}</span>
                        {dayOffset === 0 && isOverdue(t) && (
                          <span data-testid={`kiosk-task-overdue-${t.id}`} className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-black font-head text-white" style={{ background: "#EF4444" }}><Clock className="w-3.5 h-3.5" strokeWidth={3} />{kt("Verlopen")}</span>
                        )}
                        {!t.postponed_at && <span data-testid={`kiosk-task-date-${t.id}`} className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-black font-head" style={isOverdue(t) ? { background: "#FEE2E2", color: "#B91C1C" } : { background: "rgba(0,0,0,0.08)" }}><CalendarDays className="w-3.5 h-3.5" strokeWidth={3} />{weekdayDayLabel(taskDate(t), loc)}</span>}
                        {t.postponed_at && (
                          <span data-testid={`kiosk-task-postponed-${t.id}`} className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-black font-head" style={{ background: "#F5C842", color: "#1a1a1a" }}><CalendarDays className="w-3.5 h-3.5" strokeWidth={3} />{kt("Uitgesteld")}</span>
                        )}
                        {t.auto_assigned && (
                          <span data-testid={`kiosk-task-auto-${t.id}`} className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-black font-head" style={{ background: "var(--k-accent)", color: "var(--k-accent-ink)" }}>🤖 {kt("Auto")}</span>
                        )}
                        {t.assign_note && (
                          <span data-testid={`kiosk-task-note-${t.id}`} title={t.assign_note} className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-black font-head" style={{ background: "var(--k-accent)", color: "var(--k-accent-ink)" }}><Mail className="w-3.5 h-3.5" strokeWidth={3} />{kt("Briefje")}</span>
                        )}
                      </div>
                      {!me && (
                        <div className="flex -space-x-2 shrink-0">
                          {t.assignees?.length > 0
                            ? t.assignees.slice(0, 3).map((a, idx) => (
                                a.avatar_url
                                  ? <img key={idx} src={a.avatar_url} alt={a.name} title={a.name} className="w-8 h-8 rounded-full object-cover border-2" style={{ borderColor: theme.vars["--k-panel"] }} />
                                  : <span key={idx} title={a.name} className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-head font-black k-accent border-2" style={{ borderColor: theme.vars["--k-panel"] }}>{a.name?.[0]?.toUpperCase()}</span>
                              ))
                            : <span title={kt("Niemand toegewezen")} data-testid={`kiosk-task-unassigned-${t.id}`} className="w-8 h-8 rounded-full flex items-center justify-center text-base font-head font-black border-2" style={{ borderColor: theme.vars["--k-panel"], background: "rgba(0,0,0,0.08)" }}>?</span>}
                        </div>
                      )}
                    </div>
                    <h3 className="font-head font-bold text-lg mt-1 leading-tight">{t.title}</h3>
                    {t.postponed_at && <p data-testid={`kiosk-postponed-${t.id}`} className="text-xs font-bold mt-0.5 inline-flex items-center gap-1" style={{ color: "var(--k-accent)" }}><CalendarDays className="w-3.5 h-3.5" strokeWidth={2.5} />{kt("Uitgesteld")}{t.postpone_reason ? `: ${t.postpone_reason}` : ""}</p>}
                    {!me && t.assignee_names?.length > 0 && <p className="text-xs k-muted font-bold mt-0.5">{t.assignee_names.join(", ")}</p>}
                  </button>
                );
              })}
            </div>
          </div>
        );
      case "mededelingen":
        return (on("mededelingen") && restAnns.length > 0) ? (
          <div key="mededelingen" className="k-card rounded-3xl p-4" style={wStyle("meldingen")} data-testid="kiosk-announcements">
            <h2 className="font-head text-lg font-black mb-2 flex items-center gap-2"><Megaphone className="w-5 h-5" strokeWidth={3} style={{ color: "var(--k-accent)" }} />{kt("Mededelingen")}</h2>
            <div className="space-y-2">
              {restAnns.map((a) => (
                <div key={a.id} className="k-pill rounded-xl p-2.5"><p className="font-head font-bold leading-tight flex items-center gap-1.5">{a.pinned && (!a.pinned_until || a.pinned_until >= new Date().toISOString().slice(0, 10)) && <Pin className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} style={{ color: "var(--k-accent)" }} />}{a.title}</p>{a.description && <p className="text-sm mt-0.5 opacity-80">{a.description}</p>}
                  {a.event_from && (
                    <button data-testid={`kiosk-event-qr-${a.id}`} onClick={() => setEventQr(a)}
                      className="mt-1.5 brutal-press rounded-full px-2.5 py-1 text-xs font-bold flex items-center gap-1" style={{ background: "var(--k-accent)", color: "#fff" }}>
                      <CalendarDays className="w-3.5 h-3.5" strokeWidth={2.5} />{a.event_from}{a.event_from_time ? ` · ${a.event_from_time}` : ""} — {kt("Zet in agenda")}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : null;
      case "nieuws":
        return (on("nieuws") && board.stories?.length > 0) ? (
          <div key="nieuws" className="k-card rounded-3xl p-4" style={wStyle("nieuws")} data-testid="kiosk-nieuws">
            <h2 className="font-head text-lg font-black mb-2 flex items-center gap-2"><Newspaper className="w-5 h-5" strokeWidth={3} style={{ color: "var(--k-accent)" }} />{kt("Nieuws")}</h2>
            <div className="space-y-2">
              {board.stories.slice(0, 4).map((s) => (
                <button key={s.id} data-testid={`kiosk-story-${s.id}`} onClick={() => setStoryView(s)}
                  className="w-full k-pill rounded-xl p-2 flex items-center gap-3 text-left active:scale-[0.98] transition-transform">
                  {s.image_url && <img src={s.image_url.startsWith("http") ? s.image_url : `${process.env.REACT_APP_BACKEND_URL}${s.image_url}`} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="font-head font-bold leading-tight truncate">{s.title}</p>
                    {s.text && <p className="text-sm opacity-80 line-clamp-2">{s.text}</p>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : null;
      case "poll":
        return (on("polls") && board.latest_poll) ? (
          <div key="poll" className="k-accent rounded-3xl p-4" style={wStyle("poll")} data-testid="kiosk-poll">
            <div className="flex items-start justify-between gap-2">
              <h2 className="font-head text-lg font-black flex items-center gap-2"><UtensilsCrossed className="w-5 h-5" strokeWidth={3} />{board.latest_poll.question}</h2>
              {Object.keys(board.latest_poll.votes || {}).length > 0 && (
                <button data-testid="kiosk-poll-voters-btn" onClick={() => setPollVoters(board.latest_poll)}
                  className="k-pill brutal-press rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 shrink-0 active:scale-95 transition-transform">
                  <User className="w-3.5 h-3.5" strokeWidth={2.5} />{kt("Wie stemde?")}
                </button>
              )}
            </div>
            {(() => {
              const votes = board.latest_poll.votes || {};
              const eatAlong = Object.values(votes).filter((v) => v !== "__afwezig__").length;
              return eatAlong > 0 ? (
                <span data-testid="kiosk-poll-eatalong" className="inline-flex items-center gap-1.5 mt-2 k-card rounded-full px-3 py-1 text-sm font-head font-black">
                  <UtensilsCrossed className="w-4 h-4" strokeWidth={2.5} style={{ color: "var(--k-accent)" }} />{eatAlong} {kt("eten mee")}
                </span>
              ) : null;
            })()}
            <div className="mt-2 space-y-2">
              {board.latest_poll.options.map((o, oi) => {
                const votes = board.latest_poll.votes || {};
                const c = Object.values(votes).filter((v) => v === o).length;
                const rawImg = (board.latest_poll.option_images || [])[oi];
                const img = rawImg ? (rawImg.startsWith("http") ? rawImg : `${process.env.REACT_APP_BACKEND_URL}${rawImg}`) : null;
                return (
                  <button key={o} data-testid={`kiosk-vote-${o}`}
                    onClick={() => me ? api.post(`/polls/${board.latest_poll.id}/vote`, { option: o }).then(loadBoard) : startFlow({ action: "vote", pollId: board.latest_poll.id, option: o, title: o })}
                    className="w-full k-card rounded-xl px-3 py-2.5 font-head font-bold flex items-center gap-3 active:scale-[0.98] transition-transform">
                    {img && (
                      <img src={img} alt="" data-testid={`kiosk-poll-img-${oi}`}
                        onClick={(e) => { e.stopPropagation(); setZoomImg(img); }}
                        className="w-12 h-12 rounded-lg object-cover shrink-0 cursor-zoom-in" />
                    )}
                    <span className="flex-1 text-left truncate">{o}</span><span className="shrink-0">{c}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null;
      case "cook_tonight":
        return (on("polls") && board.cook_tonight?.length > 0) ? (
          <div key="cook_tonight" className="k-card rounded-3xl p-4" style={wStyle("poll")} data-testid="kiosk-cook-tonight">
            {board.cook_tonight.map((c, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`kiosk-cook-tonight-${i}`}>
                <span className="text-3xl shrink-0">🍳</span>
                <div className="min-w-0">
                  <p className="font-head font-black text-lg leading-tight">{kt("Kookt vanavond")}: {c.assignees.map((a) => a.name).join(" & ")}</p>
                  <p className="text-sm k-muted font-bold truncate">{c.title}</p>
                </div>
                <div className="flex -space-x-2 ml-auto shrink-0">
                  {c.assignees.map((a, j) => (
                    a.avatar_url ? <img key={j} src={a.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover brutal-sm" />
                      : <span key={j} className="w-10 h-10 rounded-full brutal-sm k-accent flex items-center justify-center font-head font-black">{a.name?.[0]?.toUpperCase()}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : null;
      case "cook_proposal":
        if (!(on("polls") && board.cook_proposal)) return null;
        return (() => {
          const cp = board.cook_proposal;
          const srv = cookServings ?? cp.eat_along ?? 1;
          const setSrv = (n) => setCookServings(Math.max(1, n));
          return (
            <div key="cook_proposal" className="k-accent rounded-3xl p-4" style={wStyle("poll")} data-testid="kiosk-cook-proposal">
              <h2 className="font-head text-lg font-black flex items-center gap-2"><UtensilsCrossed className="w-5 h-5" strokeWidth={3} />{kt("Poll gesloten — plan het koken")}</h2>
              <p className="font-head font-bold mt-1">{kt("Winnaar")}: {cp.winner}</p>
              <div className="mt-3 k-card rounded-2xl p-3 flex items-center justify-between">
                <span className="font-head font-bold">{kt("Aantal personen")}</span>
                <div className="flex items-center gap-3">
                  <button data-testid="kiosk-cook-minus" onClick={() => setSrv(srv - 1)} className="k-pill brutal-press w-10 h-10 rounded-full text-2xl font-head font-black flex items-center justify-center active:scale-90 transition-transform">−</button>
                  <span data-testid="kiosk-cook-servings" className="font-head font-black text-2xl w-8 text-center">{srv}</span>
                  <button data-testid="kiosk-cook-plus" onClick={() => setSrv(srv + 1)} className="k-pill brutal-press w-10 h-10 rounded-full text-2xl font-head font-black flex items-center justify-center active:scale-90 transition-transform">+</button>
                </div>
              </div>
              <button data-testid="kiosk-plan-cook-btn" onClick={() => startFlow({ action: "planCook", pollId: cp.poll_id, servings: srv, title: cp.winner })}
                className="mt-3 w-full k-card brutal-press rounded-full px-4 py-3 font-head font-black flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
                <UtensilsCrossed className="w-5 h-5" strokeWidth={2.5} style={{ color: "var(--k-accent)" }} />{kt("Plan kooktaak in")}
              </button>
              <p className="text-xs k-muted font-semibold mt-2 text-center">{kt("Er wordt gevraagd wie kookt (niemand vast toegewezen).")}</p>
            </div>
          );
        })();
      case "vieringen":
        return (on("vieringen") && board.celebrations?.length > 0) ? (
          <div key="vieringen" className="k-card rounded-3xl p-4" style={wStyle("vieringen")} data-testid="kiosk-celebrations">
            <h2 className="font-head text-lg font-black mb-2 flex items-center gap-2"><PartyPopper className="w-5 h-5" strokeWidth={3} style={{ color: "var(--k-accent)" }} />{kt("Vieringen")}</h2>
            <div className="space-y-2">
              {board.celebrations.map((c, i) => (
                <div key={i} className="k-pill rounded-xl p-2.5 flex items-center gap-2.5">
                  {c.image_url
                    ? <img src={c.image_url} alt="" className="w-10 h-10 rounded-full object-cover brutal-sm shrink-0" />
                    : <PartyPopper className="w-5 h-5 shrink-0" strokeWidth={2.5} style={{ color: "var(--k-accent)" }} />}
                  <div><p className="font-head font-bold leading-tight">{c.title}</p><p className="text-xs k-muted font-bold capitalize">{relDate(c.date, kt, loc)}</p></div></div>
              ))}
            </div>
          </div>
        ) : null;
      case "nu_log": {
        const all = board?.recent_nudges || [];
        const logs = all.filter((n) => !n.done || (n.resolved_at && Date.now() - new Date(n.resolved_at).getTime() < 3600000));
        return logs.length > 0 ? (
          <div key="nu_log" className="k-card rounded-3xl p-4" style={wStyle("nu_log")} data-testid="kiosk-nu-log">
            <h2 className="font-head text-lg font-black mb-2 flex items-center gap-2"><Siren className="w-5 h-5" strokeWidth={3} style={{ color: "#EF4444" }} />{kt("NU!-logboek")}</h2>
            <div className="space-y-2">
              {logs.slice(0, 6).map((n) => {
                const mins = Math.max(0, Math.round((Date.now() - new Date(n.created_at).getTime()) / 60000));
                const ago = mins < 1 ? kt("zojuist") : mins < 60 ? `${mins} ${kt("min geleden")}` : `${Math.round(mins / 60)} ${kt("uur geleden")}`;
                return (
                  <div key={n.id} className={`k-pill rounded-xl p-2.5 flex items-center justify-between gap-2 ${n.done ? "opacity-60" : ""}`} data-testid={`kiosk-nu-log-${n.id}`}>
                    <div className="min-w-0">
                      <p className={`font-head font-bold leading-tight truncate ${n.done ? "line-through" : ""}`}>{n.task_title}</p>
                      <p className="text-xs k-muted font-bold">{kt("door")} {n.actor_name} · {ago}</p>
                    </div>
                    {n.done ? (
                      <span className="shrink-0 inline-flex items-center gap-1 text-xs font-black rounded-full px-2.5 py-1 bg-brand-ink/10 text-brand-ink/60"><Check className="w-3.5 h-3.5" strokeWidth={3} />{n.done_by_name ? `${n.done_by_name}${n.done_delay_min != null ? ` · ${n.done_delay_min < 1 ? kt("meteen") : n.done_delay_min < 60 ? `${n.done_delay_min} ${kt("min later")}` : `${Math.round(n.done_delay_min / 60)} ${kt("uur later")}`}` : ""}` : kt("Afgehandeld")}</span>
                    ) : (
                      <span className="shrink-0 inline-flex items-center gap-1 text-xs font-black rounded-full px-2.5 py-1" style={{ background: "#FEF3C7", color: "#92400E" }}><Clock className="w-3.5 h-3.5" strokeWidth={3} />{kt("Nog open")}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null;
      }
      case "pindates": {
        const _pd = new Date();
        const _today = `${_pd.getFullYear()}-${String(_pd.getMonth() + 1).padStart(2, "0")}-${String(_pd.getDate()).padStart(2, "0")}`;
        const upcomingPins = (board.pindates || []).filter((p) => String(p.event_date).slice(0, 10) >= _today).sort((a, b) => (String(a.event_date || "") + (a.event_time || "99:99")).localeCompare(String(b.event_date || "") + (b.event_time || "99:99")));
        return (on("pindates") && upcomingPins.length > 0) ? (
          <div key="pindates" className="k-card rounded-3xl p-4" style={wStyle("pindates")} data-testid="kiosk-pindates">
            <h2 className="font-head text-lg font-black mb-2 flex items-center gap-2"><Pin className="w-5 h-5" strokeWidth={3} style={{ color: "var(--k-accent)" }} />{kt("Pin Dates")}</h2>
            <div className="space-y-2">
              {upcomingPins.map((p) => {
                const parts = [];
                if (p.reminder_days_before) parts.push(p.reminder_days_before === 7 ? "1 week vooraf" : p.reminder_days_before === 1 ? "1 dag vooraf" : `${p.reminder_days_before} dagen vooraf`);
                if (p.reminder_morning ?? true) parts.push("'s ochtends");
                if (p.reminder_before_min) parts.push(p.reminder_before_min === 60 ? "1 uur vooraf" : `${p.reminder_before_min} min vooraf`);
                const nowD = new Date();
                const todayStr = `${nowD.getFullYear()}-${String(nowD.getMonth() + 1).padStart(2, "0")}-${String(nowD.getDate()).padStart(2, "0")}`;
                const isToday = String(p.event_date).slice(0, 10) === todayStr;
                let urgency = null;
                if (p.event_time && isToday) {
                  const start = new Date(`${String(p.event_date).slice(0, 10)}T${p.event_time}`);
                  const diffMin = (start - nowD) / 60000;
                  if (diffMin >= -15 && diffMin <= 60) urgency = "soon";
                  else if (diffMin > 60) urgency = "today";
                } else if (isToday) {
                  urgency = "today";
                }
                const c = pindateColor(p);
                return (
                  <div key={p.id} onClick={() => setCalPinDetail(p)} className={`k-pill rounded-xl p-2.5 cursor-pointer ${urgency === "soon" ? "ring-2 ring-rose-400" : ""} ${isToday ? "k-today-glow" : ""}`} style={{ borderLeft: `5px solid ${c.hex}`, ...(isToday ? { "--glow": `color-mix(in srgb, ${c.hex} 55%, transparent)` } : {}) }} data-testid={`kiosk-pindate-${p.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-head font-bold leading-tight">{p.title}</p>
                      {urgency === "soon" && <span data-testid={`kiosk-pindate-urgency-${p.id}`} className="shrink-0 inline-flex items-center gap-1 text-xs font-black rounded-full px-2.5 py-1 bg-rose-500 text-white animate-pulse"><Clock className="w-3.5 h-3.5" strokeWidth={3} />Zo!</span>}
                      {urgency === "today" && <span data-testid={`kiosk-pindate-urgency-${p.id}`} className="shrink-0 inline-flex items-center gap-1 text-xs font-black rounded-full px-2.5 py-1 bg-brand-yellow text-brand-ink" style={{ background: "var(--k-accent)", color: "#fff" }}><PartyPopper className="w-3.5 h-3.5" strokeWidth={3} />Vandaag!</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-xs font-bold rounded-full px-2 py-0.5" style={{ background: c.hex, color: c.ink }}><CalendarDays className="w-3.5 h-3.5" strokeWidth={2.5} />{relDate(p.event_date, kt, loc)}{pinTimeLabel(p) ? ` · ${pinTimeLabel(p)}` : ""}</span>
                      {p.recipient_names?.length > 0 && <span className="text-xs k-muted font-bold">{p.recipient_names.join(", ")}</span>}
                    </div>
                    {parts.length > 0 && <span data-testid={`kiosk-pindate-reminder-${p.id}`} className="inline-flex items-center gap-1 text-xs font-bold rounded-full px-2 py-0.5 mt-1.5" style={{ background: "var(--k-accent)", color: "#fff" }}><BellRing className="w-3.5 h-3.5" strokeWidth={2.5} />{parts.join(" · ")}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null;
      }

      case "boodschappen": {
        const items = board.shopping || [];
        return board.shopping_enabled ? (
          <div key="boodschappen" className="k-card rounded-3xl p-4" style={{ ...(wStyle("boodschappen") || {}), background: "color-mix(in srgb, var(--k-accent) 14%, var(--k-panel))" }} data-testid="kiosk-boodschappen">
            <h2 className="font-head text-lg font-black mb-2 flex items-center gap-2"><ShoppingCart className="w-5 h-5" strokeWidth={3} style={{ color: "var(--k-accent)" }} />{kt("Boodschappen")}</h2>
            <div className="space-y-2 mb-2 max-h-[40vh] overflow-auto">
              {items.length === 0 && <p className="text-sm k-muted">{kt("Lijst is leeg")}</p>}
              {SHOP_CATEGORIES.map((cat) => {
                const list = items.filter((i) => (i.category || "Overig") === cat);
                if (!list.length) return null;
                return (
                  <div key={cat}>
                    <p className="font-bold text-xs k-muted uppercase tracking-wide mb-1">{kt(cat)}</p>
                    <div className="space-y-1.5">
                      {list.map((it) => (
                        <div key={it.id} className={`k-pill rounded-xl px-3 py-2 flex items-center gap-2 ${it.checked ? "opacity-50" : ""}`}>
                          <button data-testid={`kiosk-shop-${it.id}`} onClick={() => shopFlow("shop_toggle", { itemId: it.id, title: it.name })}
                            className="flex items-center gap-2 flex-1 min-w-0 text-left">
                            <span className="w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0" style={{ borderColor: "var(--k-accent)", background: it.checked ? "var(--k-accent)" : "transparent" }}>{it.checked && <Check className="w-3.5 h-3.5" strokeWidth={3} />}</span>
                            <span className={`font-bold flex-1 min-w-0 truncate ${it.checked ? "line-through" : ""}`}>{it.name}{it.quantity ? ` (${it.quantity})` : ""}</span>
                            {it.added_by_name && <span className="text-xs k-muted shrink-0">{it.added_by_name}</span>}
                          </button>
                          {!it.checked && (
                            <div className="flex items-center gap-1 shrink-0">
                              <button data-testid={`kiosk-shop-minus-${it.id}`} onClick={() => shopFlow("shop_qty", { itemId: it.id, quantity: stepQuantity(it.quantity, -1), title: it.name })} className="rounded-full w-8 h-8 flex items-center justify-center brutal-press" style={{ background: "var(--k-accent)", color: "var(--k-accent-ink)" }}><Minus className="w-4 h-4" strokeWidth={3} /></button>
                              <button data-testid={`kiosk-shop-plus-${it.id}`} onClick={() => shopFlow("shop_qty", { itemId: it.id, quantity: stepQuantity(it.quantity, 1), title: it.name })} className="rounded-full w-8 h-8 flex items-center justify-center brutal-press" style={{ background: "var(--k-accent)", color: "var(--k-accent-ink)" }}><Plus className="w-4 h-4" strokeWidth={3} /></button>
                            </div>
                          )}
                          <button data-testid={`kiosk-shop-del-${it.id}`} onClick={() => shopFlow("shop_delete", { itemId: it.id, title: it.name })} className="rounded-full w-8 h-8 flex items-center justify-center brutal-press shrink-0" style={{ background: "var(--k-accent)", color: "var(--k-accent-ink)" }}><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            {items.length > 0 && (
              <div className="flex gap-2 mb-2">
                {items.some((i) => i.checked) && (
                  <button data-testid="kiosk-shop-clear" onClick={() => setShopClearN(items.filter((i) => i.checked).length)} className="flex-1 k-pill rounded-xl px-3 py-2 font-bold text-sm flex items-center justify-center gap-2 brutal-press"><Trash2 className="w-4 h-4" strokeWidth={2.5} />{kt("Afgevinkte wissen")}</button>
                )}
                <button data-testid="kiosk-shop-clear-all" onClick={() => setShopClearAll(items.length)} className="flex-1 k-pill rounded-xl px-3 py-2 font-bold text-sm flex items-center justify-center gap-2 brutal-press"><Trash2 className="w-4 h-4" strokeWidth={2.5} />{kt("Alles wissen")}</button>
              </div>
            )}
            <div className="flex gap-1.5">
              <input data-testid="kiosk-shop-input" value={shopAddName} onChange={(e) => setShopAddName(e.target.value)} placeholder={kt("Item toevoegen…")} className="flex-1 min-w-0 k-pill rounded-xl px-3 py-2 font-medium outline-none" />
              <input data-testid="kiosk-shop-qty" value={shopAddQty} onChange={(e) => setShopAddQty(e.target.value)} placeholder={kt("Aantal")} className="w-20 shrink-0 k-pill rounded-xl px-3 py-2 font-medium outline-none" />
              <button data-testid="kiosk-shop-add" disabled={!shopAddName.trim()} onClick={() => { const n = shopAddName.trim(); const q = shopAddQty.trim(); setShopAddName(""); setShopAddQty(""); shopFlow("shop_add", { name: n, quantity: q }); }} className="k-accent brutal-press rounded-xl px-3 py-2 font-black disabled:opacity-40"><Plus className="w-5 h-5" strokeWidth={3} /></button>
            </div>
          </div>
        ) : null;
      }

      case "swaps":
        return (on("taakruilingen") && board.open_swaps?.length > 0) ? (
          <div key="swaps" className="k-card rounded-3xl p-4" style={wStyle("swaps")} data-testid="kiosk-swaps">
            <h2 className="font-head text-lg font-black mb-2 flex items-center gap-2"><Repeat className="w-5 h-5" strokeWidth={3} style={{ color: "var(--k-accent)" }} />{kt("Openstaande taakruilingen")}</h2>
            <div className="space-y-2">
              {board.open_swaps.map((s) => (
                <div key={s.id} className="k-pill rounded-xl p-2.5" data-testid={`kiosk-swap-${s.id}`}>
                  <p className="font-head font-bold leading-tight">{s.task_title}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-xs font-bold rounded-full px-2 py-0.5 k-accent"><Clock className="w-3.5 h-3.5" strokeWidth={2.5} />{swapWhen(s)}</span>
                    <span className="text-xs k-muted font-bold">{s.requester_name || kt("Iemand")}{s.responses?.length ? ` · ${s.responses.length} ${kt("reactie(s)")}` : ""}</span>
                  </div>
                  <button data-testid={`kiosk-swap-take-${s.id}`} onClick={() => startFlow({ action: "takeSwap", swapId: s.id, title: s.task_title })}
                    className="mt-2 w-full k-accent brutal-press rounded-full px-3 py-2 font-head font-bold text-sm flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform">
                    <Hand className="w-4 h-4" strokeWidth={2.5} />{kt("Ja, ik doe dit wel")}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : null;
      case "modules":
        return moreTiles.length > 0 ? (
          <div key="modules" className="k-card rounded-3xl p-4" style={wStyle("modules")} data-testid="kiosk-module-tiles">
            <h2 className="font-head text-lg font-black mb-2">{kt("Meer")}</h2>
            <div className="grid grid-cols-2 gap-2">
              {moreTiles.map((m) => {
                const Icon = m.icon;
                return (
                  <button key={m.key} data-testid={`kiosk-module-${m.key}`}
                    onClick={() => setMoreKey(m.key)}
                    className="k-pill brutal-press rounded-2xl p-3 flex items-center gap-2 active:scale-[0.97] transition-transform">
                    <Icon className="w-5 h-5 shrink-0" strokeWidth={2.5} style={{ color: "var(--k-accent)" }} />
                    <span className="font-head font-bold">{kt(m.label)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null;
      case "qr":
        return (board.kiosk_show_phone_tile !== false) ? (
          <div key="qr" className="k-card rounded-3xl p-4 flex items-center gap-4" data-testid="kiosk-qr-card">
            <div className="bg-white rounded-2xl p-2 brutal-sm shrink-0">
              <img src={`${API}/app-qr?base_url=${encodeURIComponent(window.location.origin)}&path=/`} alt="QR-code Slackr" className="w-24 h-24" data-testid="kiosk-qr-img" />
            </div>
            <div>
              <h2 className="font-head text-lg font-black flex items-center gap-2"><QrCode className="w-5 h-5" strokeWidth={2.5} style={{ color: "var(--k-accent)" }} />{kt("Op je telefoon")}</h2>
              <p className="text-sm k-muted font-semibold mt-0.5">{kt("Scan om Slackr op je telefoon te openen of je aan te melden.")}</p>
              <button data-testid="kiosk-agenda-subscribe-btn" onClick={() => startFlow({ action: "subscribe" })}
                className="mt-2 brutal-press rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5" style={{ background: "var(--k-accent)", color: "#fff" }}>
                <CalendarDays className="w-3.5 h-3.5" strokeWidth={2.5} />{kt("Abonneer op je agenda")}
              </button>
            </div>
          </div>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div dir={board.kiosk_language === "ar" ? "rtl" : "ltr"} className="k-screen min-h-screen pb-6" style={theme.vars} data-testid="kiosk-board">
      <header className="k-header rounded-3xl mx-4 mt-4 px-6 h-[72px] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="brutal-sm rounded-xl azure-gradient text-white w-11 h-11 flex items-center justify-center"><Sparkles className="w-6 h-6" strokeWidth={3} /></div>
          {(board.org_icon_emoji || board.org_icon_url) && (
            <div className="brutal-sm rounded-xl bg-white w-11 h-11 flex items-center justify-center text-2xl overflow-hidden" data-testid="kiosk-org-icon">
              <OrgIconContent emoji={board.org_icon_emoji} url={board.org_icon_url} />
            </div>
          )}
          <div><span className="font-head font-black text-2xl tracking-tight">Slackr</span>
            <p className="text-xs k-muted font-bold -mt-1">{me ? `${kt("Ingelogd als")} ${me.name}` : (board.org_name || kt("Infobord — voor het hele huis"))}</p></div>
          {!me && (
            <button data-testid="kiosk-calendar-open" onClick={() => { setCalView(board.default_calendar_view || "week"); setCalCursor(new Date()); setCalOpen(true); }}
              className="brutal-press rounded-full px-4 py-2.5 font-head font-black flex items-center gap-2 shrink-0 ml-1 animate-slow-flash" style={{ background: "#F5C842", color: "#1a1a1a" }}><CalendarDays className="w-5 h-5" strokeWidth={2.5} />{kt("Agenda")}</button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!me && <UpdatedPill ts={lastUpdated} pulse={pulse} kt={kt} />}
          <Tip label={kt("Stuur iedereen 'kom in actie'")}><button data-testid="kiosk-action-reminder-btn" onClick={() => { setArAll(true); setArSel([]); setArOpen(true); }} className="k-pill brutal-press rounded-full px-4 py-2.5 font-head font-bold flex items-center gap-2">📣 {kt("Kom in actie")}</button></Tip>
          {locked ? (
            <button data-testid="kiosk-unlock-btn" title={kt("Kioskmodus verlaten")} aria-label={kt("Kioskmodus verlaten")} onClick={() => startFlow({ action: "unlock" })} className="k-accent brutal-press rounded-full p-2.5 flex items-center justify-center"><Lock className="w-5 h-5" strokeWidth={2.5} /></button>
          ) : (
            <Tip label={kt("Kioskmodus vergrendelen (fullscreen)")}><button data-testid="kiosk-lock-btn" onClick={lockKiosk} className="k-pill brutal-press rounded-full px-4 py-2.5 font-head font-bold flex items-center gap-2"><Unlock className="w-5 h-5" strokeWidth={2.5} />{kt("Vergrendelen")}</button></Tip>
          )}
          <Tip label={kt("App installeren / aansluiten (QR)")}><button data-testid="kiosk-install-qr-btn" onClick={() => setInstallQrOpen(true)} className="k-pill brutal-press rounded-full p-3"><QrCode className="w-5 h-5" strokeWidth={2.5} /></button></Tip>
          {kioskOrg && <Tip label={kt("Spelregels lezen (QR)")}><button data-testid="kiosk-rules-qr-btn" onClick={() => setRulesQrOpen(true)} className="k-pill brutal-press rounded-full p-3"><BookOpen className="w-5 h-5" strokeWidth={2.5} /></button></Tip>}
          <Tip label={kt("Thema kiezen")}><button data-testid="kiosk-theme-btn" onClick={() => setThemeOpen(true)} className="k-pill brutal-press rounded-full p-3"><Palette className="w-5 h-5" strokeWidth={2.5} /></button></Tip>
          {board.kiosk_show_wheel !== false && <Tip label={kt("Rad van Fortuin")}><button data-testid="kiosk-wheel-btn" onClick={() => startFlow({ action: "spin" })} className="k-pill brutal-press rounded-full p-3 text-xl leading-none">🎡</button></Tip>}
          {me ? (
            <>
              <Tip label={kt("Meldingsvoorkeuren")}><button data-testid="kiosk-prefs-btn" onClick={() => setPrefsOpen(true)} className="k-pill brutal-press rounded-full p-3"><Bell className="w-5 h-5" strokeWidth={2.5} /></button></Tip>
              <button data-testid="kiosk-logout" onClick={exitPersonal} className="k-pill brutal-press rounded-full px-4 py-2.5 font-head font-bold flex items-center gap-2"><LogOut className="w-5 h-5" strokeWidth={2.5} />{kt("Naar bord")}</button>
            </>
          ) : (
            <button data-testid="kiosk-login-btn" title={kt("Inloggen met PIN")} aria-label={kt("Inloggen met PIN")} onClick={() => startFlow({ action: "login" })} className="k-accent brutal-press rounded-full p-2.5 flex items-center justify-center"><User className="w-5 h-5" strokeWidth={2.5} /></button>
          )}
        </div>
      </header>

      {on("vieringen") && board.birthdays_today?.length > 0 && (
        <div className="mx-4 mt-4 k-accent rounded-3xl px-6 py-4 flex items-center gap-4 animate-pop" style={themeId === "doenies" ? { "--k-accent": "#371F75", "--k-accent-ink": "#FFFFFF" } : undefined} data-testid="kiosk-birthday-card">
          <PartyPopper className="w-9 h-9 shrink-0" strokeWidth={2.5} />
          <div>
            <p className="font-head font-black text-2xl leading-tight">{kt("Hoera!")} {board.birthdays_today.join(" & ")} {kt("is jarig! 🎉")}</p>
            <p className="text-sm opacity-80 font-semibold">{kt("Vergeet niet te feliciteren.")}</p>
          </div>
        </div>
      )}

      {!me && board.allowance?.members?.length > 0 && <BonusSpinBanner members={board.allowance.members} kt={kt} />}

      {on("mededelingen") && !me && pinnedAnns.length > 0 && (
        <AnnouncementBanner items={pinnedAnns} kt={kt} onEvent={setEventQr} />
      )}

      <div className="px-4 mt-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 flex flex-col gap-4">
          {mainCol.map((id) => renderBlock(id))}
        </div>
        <div className="lg:col-span-4 flex flex-col gap-4">
          {sideCol.map((id) => renderBlock(id))}
        </div>
      </div>

      {/* Taakdetail pop-up */}
      {taskDetail && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-xl flex items-center justify-center p-4" onClick={() => setTaskDetail(null)} data-testid="kiosk-task-detail">
          <div className="k-card rounded-[2rem] p-6 w-full max-w-md" style={theme.vars} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                {(() => { const m = dueMeta(taskDetail.due_when); const I = m.icon; return (
                  <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-bold font-head k-accent"><I className="w-4 h-4" strokeWidth={3} />{taskDetail.postponed_at && (taskDetail.original_date || taskDetail.next_date) ? new Date((taskDetail.original_date || taskDetail.next_date) + "T00:00:00").toLocaleDateString(loc, { weekday: "short", day: "numeric", month: "short" }) : (taskDetail.due_when === "specifiek" ? taskDetail.due_time : kt(m.label))}</span>
                ); })()}
                <h3 className="font-head font-black text-2xl mt-2 leading-tight">{taskDetail.title}</h3>
              </div>
              <button data-testid="kiosk-detail-close" onClick={() => setTaskDetail(null)} className="k-pill brutal-press rounded-full p-2 shrink-0"><X className="w-5 h-5" strokeWidth={2.5} /></button>
            </div>
            {taskDetail.description && <p className="mt-3 opacity-80 font-medium">{taskDetail.description}</p>}
            {taskDetail.postponed_at && <p data-testid="kiosk-detail-postponed" className="mt-3 text-sm font-bold inline-flex items-center gap-1.5 k-pill rounded-full px-3 py-1.5"><CalendarDays className="w-4 h-4" strokeWidth={2.5} style={{ color: "var(--k-accent)" }} />{kt("Uitgesteld naar")} {relDate(taskDetail.next_date, kt, loc)}{taskDetail.postpone_reason ? ` — ${taskDetail.postpone_reason}` : ""}</p>}
            {!me && (
              <div className="flex items-center gap-2 mt-3">
                {taskDetail.assignees?.length > 0
                  ? taskDetail.assignees.map((a, idx) => (
                      <span key={idx} className="flex items-center gap-1.5 k-pill rounded-full pl-1 pr-3 py-1">
                        {a.avatar_url ? <img src={a.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" /> : <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-head font-black k-accent">{a.name?.[0]?.toUpperCase()}</span>}
                        <span className="font-head font-bold text-sm">{a.name}</span>
                      </span>
                    ))
                  : <span className="flex items-center gap-1.5 k-pill rounded-full pl-1 pr-3 py-1"><span className="w-7 h-7 rounded-full flex items-center justify-center font-head font-black" style={{ background: "rgba(0,0,0,0.08)" }}>?</span><span className="font-head font-bold text-sm k-muted">{kt("Niemand toegewezen")}</span></span>}
              </div>
            )}
            <div className="flex gap-3 mt-5">
              <button data-testid={`kiosk-complete-${taskDetail.id}`}
                onClick={() => me ? api.post(`/tasks/${taskDetail.id}/complete`).then(() => { loadMy(); setTaskDetail(null); }) : startFlow({ action: "complete", taskId: taskDetail.id, title: taskDetail.title })}
                className="flex-1 k-accent brutal-press rounded-full px-4 py-3 font-head font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"><Check className="w-5 h-5" strokeWidth={3} />{kt("Klaar")}</button>
              {!me && (<>
                {!(taskDetail.assignee_names?.length) && <button data-testid={`kiosk-ai-assign-${taskDetail.id}`} onClick={() => aiAssign(taskDetail.id)}
                  className="flex-1 k-accent brutal-press rounded-full px-4 py-3 font-head font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"><Sparkles className="w-5 h-5" strokeWidth={2.5} />{kt("AI wijst toe")}</button>}
                <button data-testid={`kiosk-take-${taskDetail.id}`} onClick={() => startFlow({ action: "take", taskId: taskDetail.id, title: taskDetail.title })}
                  className="flex-1 k-pill brutal-press rounded-full px-4 py-3 font-head font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"><Hand className="w-5 h-5" strokeWidth={2.5} />{kt("Overnemen")}</button>
              </>)}
            </div>
            {taskDetail.status !== "klaar" && taskDetail.status !== "wacht_op_bevestiging" && (
              <button data-testid={`kiosk-postpone-${taskDetail.id}`} onClick={() => { setKpDate(new Date(Date.now() + 86400000).toISOString().slice(0, 10)); setKpReason(""); setKioskPostpone(taskDetail); setTaskDetail(null); }}
                className="w-full mt-3 k-pill brutal-press rounded-full px-4 py-3 font-head font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"><CalendarDays className="w-5 h-5" strokeWidth={2.5} />{kt("Uitstellen")}</button>
            )}
            {taskDetail.status !== "klaar" && taskDetail.status !== "wacht_op_bevestiging" && (
              <button data-testid={`kiosk-nudge-${taskDetail.id}`} disabled={nudgeSending} onClick={() => kioskNudgeTask(taskDetail)}
                className="w-full mt-3 brutal-press rounded-full px-4 py-3 font-head font-black flex items-center justify-center gap-2 active:scale-95 transition-transform text-white disabled:opacity-70" style={{ background: "#EF4444" }}>
                {nudgeSending ? <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.5} /> : <Siren className="w-5 h-5" strokeWidth={2.5} />}{nudgeSending ? kt("Versturen…") : kt("NU! — moet direct gebeuren")}
              </button>
            )}
            {!me && (
              <button data-testid={`kiosk-delete-${taskDetail.id}`} onClick={async () => { const td = taskDetail; let penalty = null; try { const { data } = await api.get(`/kiosk/tasks/${td.id}/penalty-preview`); penalty = data; } catch (e) {} setConfirmDelete({ ...td, penalty }); setTaskDetail(null); }}
                className="w-full mt-3 brutal-press rounded-full px-4 py-3 font-head font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform" style={{ border: "2px solid #EF4444", color: "#EF4444" }}>
                <Delete className="w-5 h-5" strokeWidth={2.5} />{kt("Taak verwijderen")}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Poll-stemmen pop-up: wie stemde op wat */}
      {pollVoters && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-xl flex items-center justify-center p-4" onClick={() => setPollVoters(null)} data-testid="kiosk-poll-voters">
          <div className="k-card rounded-[2rem] p-6 w-full max-w-md max-h-[88vh] overflow-y-auto" style={theme.vars} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <h3 className="font-head font-black text-2xl leading-tight">{pollVoters.question}</h3>
              <button data-testid="kiosk-poll-voters-close" onClick={() => setPollVoters(null)} className="k-pill brutal-press rounded-full p-2 shrink-0"><X className="w-5 h-5" strokeWidth={2.5} /></button>
            </div>
            <div className="space-y-3">
              {pollVoters.options.map((o, i) => {
                const voters = pollVoters.voter_groups?.options?.[i] || [];
                return (
                  <div key={o} className="k-pill rounded-2xl p-3" data-testid={`kiosk-voters-option-${i}`}>
                    <p className="font-head font-bold flex items-center justify-between"><span>{o}</span><span className="k-muted">{voters.length}</span></p>
                    {voters.length > 0 ? (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {voters.map((v, j) => (
                          <span key={j} className="flex items-center gap-1.5 k-card rounded-full pl-1 pr-3 py-1">
                            {v.avatar_url ? <img src={v.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" /> : <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-head font-black k-accent">{v.name?.[0]?.toUpperCase()}</span>}
                            <span className="font-head font-bold text-sm">{v.name}</span>
                          </span>
                        ))}
                      </div>
                    ) : <p className="text-sm k-muted font-semibold mt-1">{kt("Nog niemand")}</p>}
                  </div>
                );
              })}
              {(pollVoters.voter_groups?.absent?.length > 0 || pollVoters.voter_groups?.dontcare?.length > 0) && (
                <div className="flex flex-wrap gap-4 pt-1">
                  {pollVoters.voter_groups?.absent?.length > 0 && (
                    <p className="text-sm font-bold">🚫 {kt("Afwezig")}: <span className="k-muted font-semibold">{pollVoters.voter_groups.absent.map((v) => v.name).join(", ")}</span></p>
                  )}
                  {pollVoters.voter_groups?.dontcare?.length > 0 && (
                    <p className="text-sm font-bold">🤷 {kt("Maakt niet uit")}: <span className="k-muted font-semibold">{pollVoters.voter_groups.dontcare.map((v) => v.name).join(", ")}</span></p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Jarige-banner in idle-stand */}
      {idle && board.birthdays_today?.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[95] k-accent rounded-full px-6 py-3 font-head font-black text-xl flex items-center gap-2" data-testid="kiosk-birthday-banner">
          <PartyPopper className="w-6 h-6" strokeWidth={2.5} />{board.birthdays_today.join(" & ")} {kt("is jarig! 🎉")}
        </div>
      )}

      {storyView && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-xl flex items-center justify-center p-6" onClick={() => setStoryView(null)} data-testid="kiosk-story-view">
          <div className="k-card rounded-[2rem] max-w-lg w-full max-h-[88vh] overflow-y-auto" style={theme.vars} onClick={(e) => e.stopPropagation()}>
            {storyView.image_url && <img src={storyView.image_url.startsWith("http") ? storyView.image_url : `${process.env.REACT_APP_BACKEND_URL}${storyView.image_url}`} alt="" className="w-full h-64 object-cover rounded-t-[2rem]" />}
            <div className="p-6 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <h2 className="font-head font-black text-3xl leading-tight">{storyView.title}</h2>
                <button data-testid="kiosk-story-close" onClick={() => setStoryView(null)} className="k-pill brutal-press rounded-full p-2 shrink-0"><X className="w-5 h-5" strokeWidth={2.5} /></button>
              </div>
              <p className="text-lg leading-relaxed whitespace-pre-wrap">{storyView.text}</p>
            </div>
          </div>
        </div>
      )}

      {feedQr && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-xl flex items-center justify-center p-6" onClick={() => setFeedQr(null)} data-testid="kiosk-feed-qr-panel">
          <div className="k-card rounded-[2rem] p-6 max-w-sm w-full text-center" style={theme.vars} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head font-black text-2xl flex items-center gap-2"><CalendarDays className="w-6 h-6" strokeWidth={2.5} />{kt("Jouw agenda-abonnement")}</h3>
              <button data-testid="kiosk-feed-qr-close" onClick={() => setFeedQr(null)} className="k-pill brutal-press rounded-full p-2"><X className="w-5 h-5" strokeWidth={2.5} /></button>
            </div>
            {feedQr.name && <p className="font-head font-bold text-lg leading-tight">{kt("Hoi")} {feedQr.name}! 👋</p>}
            <div className="bg-white rounded-2xl p-3 brutal-sm inline-block mt-3">
              <img
                src={`${API}/app-qr?base_url=${encodeURIComponent(window.location.origin)}&path=${encodeURIComponent(feedQr.path)}`}
                alt="QR-code om je op je agenda te abonneren"
                className="w-56 h-56"
                data-testid="kiosk-feed-qr-img"
              />
            </div>
            <p className="text-sm k-muted font-semibold mt-3">{kt("Scan met je telefoon en abonneer je één keer — je taken, activiteiten en pin dates verschijnen daarna automatisch in je eigen agenda. Deze code is persoonlijk.")}</p>
          </div>
        </div>
      )}

      {eventQr && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-xl flex items-center justify-center p-6" onClick={() => setEventQr(null)} data-testid="kiosk-event-qr-panel">
          <div className="k-card rounded-[2rem] p-6 max-w-sm w-full text-center" style={theme.vars} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head font-black text-2xl flex items-center gap-2"><CalendarDays className="w-6 h-6" strokeWidth={2.5} />{kt("Zet in je agenda")}</h3>
              <button data-testid="kiosk-event-qr-close" onClick={() => setEventQr(null)} className="k-pill brutal-press rounded-full p-2"><X className="w-5 h-5" strokeWidth={2.5} /></button>
            </div>
            <p className="font-head font-bold text-lg leading-tight">{eventQr.title}</p>
            <p className="text-sm k-muted font-semibold mt-1">
              {eventQr.event_from}{eventQr.event_from_time ? ` · ${eventQr.event_from_time}` : ""}
              {eventQr.event_to && eventQr.event_to !== eventQr.event_from ? ` ${kt("t/m")} ${eventQr.event_to}` : ""}
              {eventQr.event_to_time ? ` – ${eventQr.event_to_time}` : ""}
            </p>
            <div className="bg-white rounded-2xl p-3 brutal-sm inline-block mt-4">
              <img
                src={`${API}/app-qr?base_url=${encodeURIComponent(window.location.origin)}&path=${encodeURIComponent(`/api/calendar/content/${eventQr.id}.ics`)}`}
                alt="QR-code om dit evenement in je agenda te zetten"
                className="w-56 h-56"
                data-testid="kiosk-event-qr-img"
              />
            </div>
            <p className="text-sm k-muted font-semibold mt-3">{kt("Scan met je telefoon en tik op openen — het evenement wordt direct aan je eigen agenda (Apple/Google/Outlook) toegevoegd.")}</p>
          </div>
        </div>
      )}

      {installQrOpen && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-xl flex items-center justify-center p-6" onClick={() => setInstallQrOpen(false)} data-testid="kiosk-install-qr-panel">
          <div className="k-card rounded-[2rem] p-6 max-w-sm w-full text-center" style={theme.vars} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head font-black text-2xl flex items-center gap-2"><QrCode className="w-6 h-6" strokeWidth={2.5} />{kt("App installeren")}</h3>
              <button data-testid="kiosk-install-qr-close" onClick={() => setInstallQrOpen(false)} className="k-pill brutal-press rounded-full p-2"><X className="w-5 h-5" strokeWidth={2.5} /></button>
            </div>
            <div className="bg-white rounded-2xl p-3 brutal-sm inline-block">
              <img
                src={`${API}/app-qr?base_url=${encodeURIComponent(window.location.origin)}&path=${encodeURIComponent(kioskOrg ? `/aanmelden?org=${kioskOrg}` : "/")}`}
                alt="QR-code om Slackr te installeren"
                className="w-56 h-56"
                data-testid="kiosk-install-qr-img"
              />
            </div>
            <p className="font-head font-bold mt-4 leading-tight">{kt("Scan met je telefoon")}</p>
            <p className="text-sm k-muted font-semibold mt-1">
              {kioskOrg
                ? kt("Je wordt direct gekoppeld aan deze organisatie en kunt de app op je telefoon installeren.")
                : kt("Open Slackr op je telefoon en installeer de app.")}
            </p>
          </div>
        </div>
      )}

      {rulesQrOpen && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-xl flex items-center justify-center p-6" onClick={() => setRulesQrOpen(false)} data-testid="kiosk-rules-qr-panel">
          <div className="k-card rounded-[2rem] p-6 max-w-sm w-full text-center" style={theme.vars} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head font-black text-2xl flex items-center gap-2"><BookOpen className="w-6 h-6" strokeWidth={2.5} />{kt("Spelregels")}</h3>
              <button data-testid="kiosk-rules-qr-close" onClick={() => setRulesQrOpen(false)} className="k-pill brutal-press rounded-full p-2"><X className="w-5 h-5" strokeWidth={2.5} /></button>
            </div>
            <div className="bg-white rounded-2xl p-3 brutal-sm inline-block">
              <img
                src={`${API}/app-qr?base_url=${encodeURIComponent(window.location.origin)}&path=${encodeURIComponent(`/api/public/spelregels/${kioskOrg}.pdf`)}`}
                alt="QR-code om de spelregels te lezen"
                className="w-56 h-56"
                data-testid="kiosk-rules-qr-img"
              />
            </div>
            <p className="font-head font-bold mt-4 leading-tight">{kt("Scan met je telefoon")}</p>
            <p className="text-sm k-muted font-semibold mt-1">{kt("Lees hoe je punten verdient, taken oppakt of doorgeeft, en welke beloningen er te winnen zijn — op je eigen telefoon.")}</p>
            <button data-testid="kiosk-rules-open-btn" onClick={() => { setRulesQrOpen(false); setRulesView(true); }}
              className="mt-4 w-full k-accent brutal-press rounded-full px-4 py-3 font-head font-black flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
              <BookOpen className="w-5 h-5" strokeWidth={2.5} />{kt("Open op dit scherm")}
            </button>
          </div>
        </div>
      )}

      {rulesView && (
        <div className="fixed inset-0 z-[105] bg-black/80 backdrop-blur-sm flex flex-col p-4 sm:p-6" data-testid="kiosk-rules-view">
          <div className="flex items-center justify-between mb-3 shrink-0">
            <h3 className="font-head font-black text-2xl text-white flex items-center gap-2"><BookOpen className="w-6 h-6" strokeWidth={2.5} />{kt("Spelregels")}</h3>
            <button data-testid="kiosk-rules-view-close" onClick={() => setRulesView(false)} className="bg-white brutal-press rounded-full px-4 py-2.5 font-head font-bold flex items-center gap-2 text-brand-ink"><X className="w-5 h-5" strokeWidth={2.5} />{kt("Sluiten")}</button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto rounded-2xl" data-testid="kiosk-rules-scroll">
            {rulesErr ? (
              <div className="h-full flex flex-col items-center justify-center text-white/80 gap-2">
                <BookOpen className="w-10 h-10" strokeWidth={1.5} />
                <p className="font-head font-bold">{kt("Kon de spelregels niet laden")}</p>
                <button onClick={() => { setRulesPages(null); setRulesErr(false); }} className="mt-2 bg-white text-brand-ink brutal-press rounded-full px-4 py-2 font-bold">{kt("Opnieuw proberen")}</button>
              </div>
            ) : !rulesPages ? (
              <div className="h-full flex flex-col items-center justify-center text-white/80 gap-3">
                <Loader2 className="w-8 h-8 animate-spin" strokeWidth={2} />
                <p className="font-head font-bold">{kt("Spelregels laden…")}</p>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto space-y-3 pb-4">
                {rulesPages.map((src, i) => (
                  <img key={i} src={src} alt={`${kt("Spelregels")} ${i + 1}`} className="w-full rounded-xl brutal-sm bg-white" data-testid={`kiosk-rules-page-${i}`} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {themeOpen && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-xl flex items-center justify-center p-6" onClick={() => setThemeOpen(false)} data-testid="kiosk-theme-panel">
          <div className="k-card rounded-[2rem] p-6 max-w-lg w-full" style={theme.vars} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 className="font-head font-black text-2xl flex items-center gap-2"><Palette className="w-6 h-6" strokeWidth={2.5} />{kt("Kies een thema")}</h3>
              <button onClick={() => setThemeOpen(false)} className="k-pill brutal-press rounded-full p-2"><X className="w-5 h-5" strokeWidth={2.5} /></button></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {KIOSK_THEMES.map((t) => (
                <button key={t.id} data-testid={`kiosk-theme-${t.id}`} onClick={() => applyTheme(t.id)} className="brutal brutal-press rounded-xl p-4 text-left active:scale-95 transition-transform" style={{ background: t.vars["--k-bg"], color: t.vars["--k-text"] }}>
                  <span className="font-head font-bold">{t.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {moreKey && (
        <KioskMore moduleKey={moreKey} title={moreLabel(moreKey)} kt={kt} loc={loc} theme={theme}
          reloadKey={moreReload} onClose={() => setMoreKey(null)} onAction={(flow) => startFlow(flow)} />
      )}
      {pinOverlay}
      {completeAttr && (() => {
        const a = completeAttr;
        const names = a.assignees.map((x) => x.name).filter(Boolean).join(", ");
        const showPicker = a.isUnassigned || attrPickOpen;
        return (
          <div className="fixed inset-0 z-[160] flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.7)" }} data-testid="kiosk-complete-attr">
            <div className="k-card w-full max-w-md p-6" style={theme.vars}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-head font-black text-2xl">{showPicker ? kt("Wie heeft 'm dan gedaan?") : kt("Wie heeft deze taak gedaan?")}</h3>
                <button data-testid="kiosk-attr-close" onClick={() => { setCompleteAttr(null); setAttrDoer(""); setAttrPickOpen(false); }} className="p-2 rounded-full" style={{ background: "var(--k-accent)", color: "var(--k-accent-ink)" }}><X className="w-5 h-5" strokeWidth={3} /></button>
              </div>
              <p className="text-sm font-semibold opacity-70 mb-4 truncate">{a.title}</p>
              {!showPicker ? (
                <div className="space-y-2">
                  {a.assignees.map((p) => (
                    <button key={p.user_id} data-testid={`kiosk-attr-behalf-${p.user_id}`} onClick={() => submitAttr({ on_behalf_of: p.user_id })}
                      className="w-full rounded-2xl p-4 font-head font-bold text-lg text-start flex items-center gap-3" style={{ background: "var(--k-accent)", color: "var(--k-accent-ink)" }}>
                      <Check className="w-6 h-6 shrink-0" strokeWidth={3} />
                      <span>{p.name} {kt("heeft 'm gedaan")}<span className="block text-sm font-semibold opacity-70">{kt("Was al gedaan, nog niet afgevinkt · punten voor")} {p.name}</span></span>
                    </button>
                  ))}
                  <div className="flex items-center gap-2 text-xs font-bold opacity-40 py-1"><div className="flex-1 h-px" style={{ background: "var(--k-text)", opacity: 0.15 }} />{kt("of")}<div className="flex-1 h-px" style={{ background: "var(--k-text)", opacity: 0.15 }} /></div>
                  <button data-testid="kiosk-attr-someone-else" onClick={() => setAttrPickOpen(true)}
                    className="w-full rounded-2xl p-4 font-head font-bold text-lg text-start flex items-center gap-3 border-2" style={{ borderColor: "var(--k-accent)", color: "var(--k-text)" }}>
                    <Repeat className="w-6 h-6 shrink-0" strokeWidth={3} />
                    <span>{kt("Iemand anders heeft 'm gedaan")}<span className="block text-sm font-semibold opacity-70">{names} {kt("deed 'm niet · strafpunten + reflectiemail")}</span></span>
                  </button>
                </div>
              ) : (
                <div>
                  {!a.isUnassigned && <p className="text-sm font-semibold opacity-70 mb-3">{names} {kt("krijgt geen punten, wél strafpunten en een reflectiemail.")}</p>}
                  <div className="grid grid-cols-2 gap-2 mb-4 max-h-[40vh] overflow-y-auto">
                    {(board?.users || []).filter((m) => !(m.role === "super_admin" || m.is_super_admin)).map((m) => {
                      const on = attrDoer === m.user_id;
                      return (
                        <button key={m.user_id} data-testid={`kiosk-attr-doer-${m.user_id}`} onClick={() => setAttrDoer(m.user_id)}
                          className="rounded-2xl p-3 font-head font-bold flex items-center gap-2 border-2"
                          style={{ background: on ? "var(--k-accent)" : "transparent", color: on ? "var(--k-accent-ink)" : "var(--k-text)", borderColor: "var(--k-accent)" }}>
                          {on ? <Check className="w-5 h-5 shrink-0" strokeWidth={3} /> : <span className="w-5 h-5 shrink-0" />}<span className="truncate">{m.name}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex gap-2">
                    {!a.isUnassigned && <button data-testid="kiosk-attr-back" onClick={() => { setAttrPickOpen(false); setAttrDoer(""); }} className="rounded-2xl px-4 py-4 font-head font-bold border-2" style={{ borderColor: "var(--k-accent)", color: "var(--k-text)" }}><ChevronLeft className="w-5 h-5" strokeWidth={3} /></button>}
                    <button data-testid="kiosk-attr-confirm" disabled={!attrDoer} onClick={() => submitAttr({ doer_id: attrDoer, neglected_ids: a.assignees.map((x) => x.user_id) })}
                      className="flex-1 rounded-2xl p-4 font-head font-black text-lg disabled:opacity-40 flex items-center justify-center gap-2" style={{ background: "var(--k-text)", color: "var(--k-panel)" }}>
                      {kt("Afvinken")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
      {assignFlash && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[200] k-accent rounded-full px-7 py-4 shadow-2xl animate-pop flex items-center gap-3 max-w-[90vw]" style={theme.vars} data-testid="kiosk-assign-flash">
          <Megaphone className="w-7 h-7 shrink-0" strokeWidth={2.5} />
          <p className="font-head font-black text-xl md:text-2xl leading-tight truncate">{assignFlash}</p>
        </div>
      )}
      {takeChoice && (() => {
        const a = takeChoice;
        const members = (board?.users || []).filter((m) => !(m.role === "super_admin" || m.is_super_admin));
        return (
          <div className="fixed inset-0 z-[160] flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.7)" }} data-testid="kiosk-take-choice">
            <div className="k-card w-full max-w-md p-6" style={theme.vars}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-head font-black text-2xl">{takeAssignOpen ? kt("Aan wie toewijzen?") : kt("Zelf overnemen of toewijzen?")}</h3>
                <button data-testid="kiosk-take-close" onClick={() => { setTakeChoice(null); setTakeAssignOpen(false); setTakeAssignee(""); setTakeNote(""); }} className="p-2 rounded-full" style={{ background: "var(--k-accent)", color: "var(--k-accent-ink)" }}><X className="w-5 h-5" strokeWidth={3} /></button>
              </div>
              <p className="text-sm font-semibold opacity-70 mb-4 truncate">{a.title}</p>
              {!takeAssignOpen ? (
                <div className="space-y-2">
                  <button data-testid="kiosk-take-self" onClick={() => submitTake({ confirm_take_self: true })}
                    className="w-full rounded-2xl p-4 font-head font-bold text-lg text-start flex items-center gap-3" style={{ background: "var(--k-accent)", color: "var(--k-accent-ink)" }}>
                    <Hand className="w-6 h-6 shrink-0" strokeWidth={3} /><span>{kt("Ik neem 'm zelf over")}</span>
                  </button>
                  <div className="flex items-center gap-2 text-xs font-bold opacity-40 py-1"><div className="flex-1 h-px" style={{ background: "var(--k-text)", opacity: 0.15 }} />{kt("of")}<div className="flex-1 h-px" style={{ background: "var(--k-text)", opacity: 0.15 }} /></div>
                  <button data-testid="kiosk-take-assign-other" onClick={() => setTakeAssignOpen(true)}
                    className="w-full rounded-2xl p-4 font-head font-bold text-lg text-start flex items-center gap-3 border-2" style={{ borderColor: "var(--k-accent)", color: "var(--k-text)" }}>
                    <Repeat className="w-6 h-6 shrink-0" strokeWidth={3} /><span>{kt("Aan iemand anders toewijzen")}<span className="block text-sm font-semibold opacity-70">{kt("Die persoon krijgt een SMS + melding")}</span></span>
                  </button>
                </div>
              ) : (
                <div>
                  <div className="grid grid-cols-2 gap-2 mb-4 max-h-[40vh] overflow-y-auto">
                    {members.map((m) => { const on = takeAssignee === m.user_id; return (
                      <button key={m.user_id} data-testid={`kiosk-take-assignee-${m.user_id}`} onClick={() => setTakeAssignee(m.user_id)}
                        className="rounded-2xl p-3 font-head font-bold flex items-center gap-2 border-2"
                        style={{ background: on ? "var(--k-accent)" : "transparent", color: on ? "var(--k-accent-ink)" : "var(--k-text)", borderColor: "var(--k-accent)" }}>
                        {on ? <Check className="w-5 h-5 shrink-0" strokeWidth={3} /> : <span className="w-5 h-5 shrink-0" />}<span className="truncate">{m.name}</span>
                      </button>
                    ); })}
                  </div>
                  <textarea data-testid="kiosk-take-note" value={takeNote} onChange={(e) => setTakeNote(e.target.value)} rows={2}
                    placeholder={kt("Kort briefje (gaat mee in SMS + melding)")} className="w-full rounded-2xl px-4 py-3 font-semibold outline-none mb-3 border-2 text-base" style={{ borderColor: "var(--k-accent)", background: "var(--k-panel)", color: "var(--k-text)" }} />
                  <div className="flex gap-2">
                    <button data-testid="kiosk-take-back" onClick={() => { setTakeAssignOpen(false); setTakeAssignee(""); setTakeNote(""); }} className="rounded-2xl px-4 py-4 font-head font-bold border-2" style={{ borderColor: "var(--k-accent)", color: "var(--k-text)" }}><ChevronLeft className="w-5 h-5" strokeWidth={3} /></button>
                    <button data-testid="kiosk-take-assign-confirm" disabled={!takeAssignee} onClick={() => submitTake({ assign_to: takeAssignee, note: takeNote })}
                      className="flex-1 rounded-2xl p-4 font-head font-black text-lg disabled:opacity-40 flex items-center justify-center gap-2" style={{ background: "var(--k-text)", color: "var(--k-panel)" }}>
                      {kt("Toewijzen")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
      {zoomImg && (
        <div className="fixed inset-0 z-[210] bg-black/90 flex items-center justify-center p-6" data-testid="kiosk-img-zoom" onClick={() => setZoomImg(null)}>
          <img src={zoomImg} alt="" className="max-w-full max-h-full rounded-2xl object-contain" />
          <button data-testid="kiosk-img-zoom-close" onClick={() => setZoomImg(null)} className="absolute top-6 right-6 bg-white/90 text-brand-ink rounded-full p-3"><X className="w-6 h-6" strokeWidth={3} /></button>
        </div>
      )}
      {assignChoice && (
        <QuickTileWizard title={assignChoice.title} users={wizardUsers} busy={assignBusy} kt={kt} loc={loc} style={theme.vars}
          onSubmit={runInstantiate} onClose={() => { if (!assignBusy) setAssignChoice(null); }} />
      )}
      {calOpen && (() => {
        const pins = board.pindates || [];
        const byd = {}; pins.forEach((p) => { (byd[p.event_date] = byd[p.event_date] || []).push(p); });
        const key = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        let days = [];
        if (calView === "week") {
          const s = new Date(calCursor); s.setDate(s.getDate() - ((s.getDay() + 6) % 7));
          for (let i = 0; i < 7; i++) { const d = new Date(s); d.setDate(s.getDate() + i); days.push(d); }
        } else {
          const first = new Date(calCursor.getFullYear(), calCursor.getMonth(), 1);
          const gs = new Date(first); gs.setDate(1 - ((first.getDay() + 6) % 7));
          for (let i = 0; i < 42; i++) { const d = new Date(gs); d.setDate(gs.getDate() + i); days.push(d); }
        }
        const shift = (n) => { const c = new Date(calCursor); if (calView === "week") c.setDate(c.getDate() + 7 * n); else c.setMonth(c.getMonth() + n); setCalCursor(c); };
        const wd = ["ma", "di", "wo", "do", "vr", "za", "zo"];
        return (
          <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-xl flex items-center justify-center p-4" onClick={() => setCalOpen(false)} data-testid="kiosk-calendar-modal">
            <div className="k-card rounded-[2rem] p-6 w-full max-w-4xl" style={theme.vars} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex gap-2">
                  <button data-testid="kiosk-cal-week" onClick={() => setCalView("week")} className={`k-pill brutal-press rounded-full px-4 py-1.5 font-bold text-sm ${calView === "week" ? "k-accent" : ""}`}>{kt("Week")}</button>
                  <button data-testid="kiosk-cal-month" onClick={() => setCalView("month")} className={`k-pill brutal-press rounded-full px-4 py-1.5 font-bold text-sm ${calView === "month" ? "k-accent" : ""}`}>{kt("Maand")}</button>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => shift(-1)} className="k-pill brutal-press rounded-full p-2"><ChevronLeft className="w-5 h-5" strokeWidth={2.5} /></button>
                  <span className="font-head font-black text-sm px-1">{calCursor.toLocaleDateString("nl-NL", { month: "long", year: "numeric" })}</span>
                  <button onClick={() => shift(1)} className="k-pill brutal-press rounded-full p-2"><ChevronRight className="w-5 h-5" strokeWidth={2.5} /></button>
                  <button data-testid="kiosk-cal-close" onClick={() => setCalOpen(false)} className="k-pill brutal-press rounded-full p-2"><X className="w-5 h-5" strokeWidth={2.5} /></button>
                </div>
              </div>
              <div className="grid grid-cols-7 gap-1 mb-1">{wd.map((w) => <p key={w} className="text-[10px] font-black text-center opacity-60">{w}</p>)}</div>
              <div className="grid grid-cols-7 gap-1">
                {days.map((d, i) => { const k = key(d); const ps = byd[k] || []; const today = k === key(new Date());
                  return (<div key={i} onClick={() => setCalAdd({ date: k, title: "", time: "", endtime: "", daypart: "", desc: "" })} data-testid={`kiosk-cal-day-${k}`} className={`rounded-lg p-1.5 cursor-pointer ${calView === "week" ? "min-h-[110px]" : "min-h-[74px]"} ${today ? "k-accent" : ""}`} style={{ background: today ? undefined : "rgba(0,0,0,0.06)" }}>
                    <p className="text-xs font-black">{d.getDate()}</p>
                    {ps.map((p) => { const c = pindateColor(p); return (<div key={p.id} onClick={(e) => { e.stopPropagation(); setCalPinDetail(p); }} data-testid={`kiosk-cal-pin-${p.id}`} className={`text-[10px] font-bold rounded px-1 py-0.5 mt-0.5 truncate cursor-pointer ${today ? "k-today-glow" : ""}`} style={{ background: c.hex, color: c.ink, ...(today ? { "--glow": `color-mix(in srgb, ${c.hex} 60%, transparent)` } : {}) }} title={p.title}>📌 {pinTimeLabel(p) ? pinTimeLabel(p) + " " : ""}{p.title}</div>); })}
                  </div>);
                })}
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs" data-testid="kiosk-cal-legend">
                <span className="font-black opacity-60">📌 {kt("Pin dates")}:</span>
                {[...PINDATE_DAYPARTS, PINDATE_NEUTRAL].map((d) => (
                  <span key={d.value} className="inline-flex items-center gap-1 font-bold opacity-80">
                    <span className="w-3 h-3 rounded-full" style={{ background: d.hex }} />{kt(d.label)}
                  </span>
                ))}
                <span title={kt("Een exacte tijd krijgt automatisch de kleur van het bijbehorende dagdeel (ochtend, middag of avond).")} data-testid="kiosk-cal-legend-info" className="inline-flex items-center cursor-help opacity-50"><Info className="w-4 h-4" strokeWidth={2.5} /></span>
              </div>
            </div>
          </div>
        );
      })()}
      {calAdd && (
        <div className="fixed inset-0 z-[115] bg-black/70 backdrop-blur-xl flex items-center justify-center p-4" onClick={() => setCalAdd(null)} data-testid="kiosk-cal-add">
          <div className="k-card rounded-[2rem] p-6 w-full max-w-md" style={theme.vars} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-head font-black text-xl">📌 {kt("Pin date toevoegen")}</h3>
              <button data-testid="kiosk-cal-add-close" onClick={() => setCalAdd(null)} className="k-pill brutal-press rounded-full p-2"><X className="w-5 h-5" strokeWidth={2.5} /></button>
            </div>
            <p className="font-bold text-sm mb-3" style={{ color: "var(--k-accent)" }}>{new Date(calAdd.date).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" })}</p>
            <input data-testid="kiosk-cal-add-title" value={calAdd.title} onChange={(e) => setCalAdd({ ...calAdd, title: e.target.value })} placeholder={kt("Titel (bv. Tandarts)")} className="w-full k-pill rounded-xl px-4 py-3 font-medium outline-none mb-2" />
            <div className="grid grid-cols-2 gap-2 mb-2">
              <label className="flex flex-col gap-1 min-w-0">
                <span className="text-xs font-bold opacity-70 px-1">{kt("Van (tijd)")}</span>
                <KTimeField testid="kiosk-cal-add-time" value={calAdd.time} onChange={(v) => setCalAdd({ ...calAdd, time: v, daypart: v ? "" : calAdd.daypart })} />
              </label>
              <label className="flex flex-col gap-1 min-w-0">
                <span className="text-xs font-bold opacity-70 px-1">{kt("Tot (optioneel)")}</span>
                <KTimeField testid="kiosk-cal-add-endtime" value={calAdd.endtime} onChange={(v) => setCalAdd({ ...calAdd, endtime: v })} />
              </label>
            </div>
            <div className="mb-2">
              <p className="text-xs font-bold opacity-70 px-1 mb-1">{kt("Of kies een dagdeel")}</p>
              <div className="grid grid-cols-4 gap-1.5">
                {[["ochtend", "Ochtend"], ["middag", "Middag"], ["avond", "Avond"]].map(([v, l]) => { const dc = PINDATE_DAYPARTS.find((x) => x.value === v); const on = !calAdd.time && calAdd.daypart === v; return (
                  <button key={v} type="button" data-testid={`kiosk-cal-add-daypart-${v}`}
                    onClick={() => setCalAdd({ ...calAdd, daypart: v, time: "", endtime: "" })}
                    style={on ? { background: dc.hex, color: dc.ink } : undefined}
                    className={`rounded-xl px-2 py-2 text-sm font-bold brutal-press ${on ? "" : "k-pill"}`}>{kt(l)}</button>
                ); })}
                <button type="button" data-testid="kiosk-cal-add-daypart-none"
                  onClick={() => setCalAdd({ ...calAdd, daypart: "", time: "", endtime: "" })}
                  className={`rounded-xl px-2 py-2 text-sm font-bold brutal-press ${!calAdd.time && !calAdd.daypart ? "k-accent" : "k-pill"}`}>{kt("Geen")}</button>
              </div>
              {(() => { const c = pindateColor({ event_time: calAdd.time, event_daypart: calAdd.daypart }); const lbl = pinTimeLabel({ event_time: calAdd.time, event_daypart: calAdd.daypart }); return (
                <div className="flex items-center gap-2 mt-2" data-testid="kiosk-cal-add-preview">
                  <span className="text-xs font-bold opacity-60">{kt("Voorbeeld in agenda:")}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-bold rounded-full px-2.5 py-0.5" style={{ background: c.hex, color: c.ink }}><CalendarDays className="w-3.5 h-3.5" strokeWidth={2.5} />{calAdd.date ? new Date(calAdd.date).toLocaleDateString("nl-NL", { day: "numeric", month: "short" }) : kt("Datum")}{lbl ? ` · ${lbl}` : ""}</span>
                </div>
              ); })()}
            </div>
            <textarea data-testid="kiosk-cal-add-desc" value={calAdd.desc} onChange={(e) => setCalAdd({ ...calAdd, desc: e.target.value })} rows={2} placeholder={kt("Omschrijving (optioneel)")} className="w-full k-pill rounded-xl px-4 py-3 font-medium outline-none mb-3" />
            <button data-testid="kiosk-cal-add-next" disabled={!calAdd.title.trim()} onClick={() => { const a = calAdd; setCalAdd(null); setPinFlow({ action: a.editId ? "edit_pindate" : "add_pindate", pinId: a.editId, event_date: a.date, title: a.title.trim(), event_time: a.time || null, event_end_time: a.endtime || null, event_daypart: a.time ? null : (a.daypart || null), description: a.desc || null }); }} className="w-full k-accent brutal-press rounded-xl py-3 font-head font-black disabled:opacity-40">{kt("Verder — voer je code in")}</button>
            <p className="text-xs opacity-60 text-center mt-2">{kt("Je voert hierna je persoonlijke code in om te bevestigen.")}</p>
          </div>
        </div>
      )}

      {shopClearN !== null && (
        <div className="fixed inset-0 z-[130] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShopClearN(null)} data-testid="kiosk-shop-clear-confirm">
          <div className="k-card rounded-[2rem] p-6 w-full max-w-sm text-center" style={theme.vars} onClick={(e) => e.stopPropagation()}>
            <Trash2 className="w-9 h-9 mx-auto mb-2" strokeWidth={2.5} style={{ color: "var(--k-accent)" }} />
            <p className="font-head font-black text-xl mb-1">{shopClearN} {shopClearN === 1 ? kt("afgevinkt item wissen?") : kt("afgevinkte items wissen?")}</p>
            <p className="text-sm k-muted mb-4">{kt("Dit kan niet ongedaan gemaakt worden.")}</p>
            <div className="flex gap-2">
              <button data-testid="kiosk-shop-clear-cancel" onClick={() => setShopClearN(null)} className="flex-1 k-pill rounded-xl py-3 font-black brutal-press">{kt("Annuleren")}</button>
              <button data-testid="kiosk-shop-clear-confirm-btn" onClick={() => { setShopClearN(null); shopFlow("shop_clear"); }} className="flex-1 k-accent rounded-xl py-3 font-black brutal-press">{kt("Ja, wissen")}</button>
            </div>
          </div>
        </div>
      )}

      {shopClearAll !== null && (
        <div className="fixed inset-0 z-[130] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShopClearAll(null)} data-testid="kiosk-shop-clearall-confirm">
          <div className="k-card rounded-[2rem] p-6 w-full max-w-sm text-center" style={theme.vars} onClick={(e) => e.stopPropagation()}>
            <Trash2 className="w-9 h-9 mx-auto mb-2" strokeWidth={2.5} style={{ color: "var(--k-accent)" }} />
            <p className="font-head font-black text-xl mb-1">{kt("Hele lijst wissen?")}</p>
            <p className="text-sm k-muted mb-4">{shopClearAll} {shopClearAll === 1 ? kt("item wordt verwijderd.") : kt("items worden verwijderd.")} {kt("Dit kan niet ongedaan gemaakt worden.")}</p>
            <div className="flex gap-2">
              <button data-testid="kiosk-shop-clearall-cancel" onClick={() => setShopClearAll(null)} className="flex-1 k-pill rounded-xl py-3 font-black brutal-press">{kt("Annuleren")}</button>
              <button data-testid="kiosk-shop-clearall-confirm-btn" onClick={() => { setShopClearAll(null); shopFlow("shop_clear_all"); }} className="flex-1 k-accent rounded-xl py-3 font-black brutal-press">{kt("Ja, alles wissen")}</button>
            </div>
          </div>
        </div>
      )}

      {kioskAiResult && (
        <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-xl flex items-center justify-center p-6" onClick={() => setKioskAiResult(null)} data-testid="kiosk-ai-result">
          <div className="k-card rounded-[2rem] p-8 w-full max-w-md text-center" style={theme.vars} onClick={(e) => e.stopPropagation()}>
            <h3 className="font-head font-black text-2xl flex items-center justify-center gap-2 mb-4" style={{ color: "var(--k-accent)" }}>
              <Sparkles className="w-6 h-6" strokeWidth={2.5} />{kt("Eerlijk toegewezen")}
            </h3>
            <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center font-head font-black text-3xl mb-3" style={{ background: "var(--k-accent)", color: "var(--k-accent-ink)" }}>{(kioskAiResult.name || "?").charAt(0)}</div>
            <p className="font-head font-black text-3xl">{kioskAiResult.name}</p>
            {kioskAiResult.reason && <p className="text-base font-semibold k-text k-pill rounded-2xl px-4 py-3 mt-3" data-testid="kiosk-ai-result-reason">{kioskAiResult.reason}</p>}
            <button data-testid="kiosk-ai-result-close" onClick={() => setKioskAiResult(null)} className="w-full k-accent brutal-press rounded-xl py-3 font-head font-black mt-5">{kt("Top, duidelijk!")}</button>
          </div>
        </div>
      )}

      {calPinDetail && (() => {
        const p = calPinDetail;
        const gal = (p.photos && p.photos.length) ? p.photos : (p.image_url ? [p.image_url] : []);
        const imgSrc = (u) => (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
        let when = String(p.event_date || "");
        try { when = new Date(p.event_date).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" }); } catch { }
        return (
          <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-xl flex items-center justify-center p-4" onClick={() => setCalPinDetail(null)} data-testid="kiosk-cal-pin-detail">
            <div className="k-card rounded-[2rem] p-6 w-full max-w-lg max-h-[85vh] overflow-auto" style={theme.vars} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-head font-black text-2xl leading-tight">📌 {p.title}</h3>
                <button data-testid="kiosk-cal-pin-close" onClick={() => setCalPinDetail(null)} className="k-pill brutal-press rounded-full p-2 shrink-0"><X className="w-5 h-5" strokeWidth={2.5} /></button>
              </div>
              {gal.length > 0 && (
                <div className={`mb-3 ${gal.length === 1 ? "" : "grid grid-cols-2 gap-2"}`}>
                  {gal.map((u, i) => <img key={i} src={imgSrc(u)} alt="" className="w-full h-44 object-cover rounded-xl" data-testid={`kiosk-cal-pin-photo-${i}`} />)}
                </div>
              )}
              <p className="font-head font-bold text-lg flex items-center gap-2" style={{ color: "var(--k-accent)" }}><CalendarDays className="w-5 h-5" strokeWidth={2.5} />{when}{pinTimeLabel(p) ? ` · ${pinTimeLabel(p)}` : ""}</p>
              {p.description && <p className="text-base mt-2 whitespace-pre-wrap k-text">{p.description}</p>}
              {p.recipient_names?.length > 0 && <p className="text-sm mt-2 k-muted"><span className="font-bold">{kt("Voor")}:</span> {p.recipient_names.join(", ")}</p>}
              {p.reminder_enabled === false && <p className="text-xs font-bold mt-2 k-muted">{kt("Alleen weergave — geen herinnering")}</p>}
              <div className="flex gap-2 mt-5 pt-4 border-t" style={{ borderColor: "rgba(0,0,0,0.1)" }}>
                <button data-testid="kiosk-cal-pin-edit" onClick={() => { setCalPinDetail(null); setCalAdd({ editId: p.id, date: p.event_date, title: p.title || "", time: p.event_time || "", endtime: p.event_end_time || "", daypart: p.event_daypart || "", desc: p.description || "" }); }}
                  className="flex-1 k-accent brutal-press rounded-xl py-3 font-head font-black flex items-center justify-center gap-2"><Pencil className="w-5 h-5" strokeWidth={2.5} />{kt("Wijzigen")}</button>
                <button data-testid="kiosk-cal-pin-delete" onClick={() => { setCalPinDetail(null); setPinFlow({ action: "delete_pindate", pinId: p.id, title: p.title }); }}
                  className="k-pill brutal-press rounded-xl px-4 py-3 font-head font-black flex items-center justify-center gap-2 text-rose-500"><Trash2 className="w-5 h-5" strokeWidth={2.5} />{kt("Verwijderen")}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {pointDetail && (() => {
        const m = pointDetail.member || {};
        const evs = pointDetail.events || [];
        return (
          <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-xl flex items-center justify-center p-4" onClick={() => setPointDetail(null)} data-testid="kiosk-point-detail">
            <div className="k-card rounded-[2rem] p-6 w-full max-w-lg max-h-[85vh] overflow-auto" style={theme.vars} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  {m.avatar_url
                    ? <img src={m.avatar_url} alt="" className="w-11 h-11 rounded-full object-cover shrink-0" />
                    : <span className="w-11 h-11 rounded-full grid place-items-center font-head font-black shrink-0" style={{ background: "var(--k-accent)", color: "var(--k-accent-ink)" }}>{(m.name || "?").slice(0, 1).toUpperCase()}</span>}
                  <div className="min-w-0">
                    <h3 className="font-head font-black text-2xl leading-tight truncate">{m.name}</h3>
                    <p className="text-sm k-muted font-semibold flex items-center gap-1"><Trophy className="w-4 h-4" strokeWidth={2.5} style={{ color: "var(--k-accent)" }} />{pointDetail.week_total ?? m.points} {kt("punten deze week")}</p>
                  </div>
                </div>
                <button data-testid="kiosk-point-detail-close" onClick={() => setPointDetail(null)} className="k-pill brutal-press rounded-full p-2 shrink-0"><X className="w-5 h-5" strokeWidth={2.5} /></button>
              </div>
              {pointDetail.loading ? (
                <p className="k-muted font-head font-bold py-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline" strokeWidth={2.5} /></p>
              ) : evs.length === 0 ? (
                <p className="k-muted font-head font-bold py-8 text-center">{kt("Nog geen punten deze week")}</p>
              ) : (
                <div data-testid="kiosk-point-detail-events">
                  {evs.map((e, i) => {
                    const d = new Date(e.created_at);
                    let dateLabel = d.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });
                    dateLabel = dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1);
                    const timeLabel = d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
                    const prev = i > 0 ? new Date(evs[i - 1].created_at) : null;
                    const newDay = !prev || prev.toDateString() !== d.toDateString();
                    const pos = e.points >= 0;
                    return (
                      <div key={i}>
                        {newDay && <p className="text-xs font-head font-black uppercase tracking-wide k-muted mt-4 mb-1.5 first:mt-0">{dateLabel}</p>}
                        <div className="flex gap-3">
                          <div className="flex flex-col items-center pt-3.5">
                            <span className="w-3.5 h-3.5 rounded-full shrink-0" style={{ background: pos ? "var(--k-accent)" : "#f43f5e" }} />
                            {i < evs.length - 1 && <span className="flex-1 w-0.5 mt-1 rounded-full" style={{ background: "rgba(148,148,163,.35)" }} />}
                          </div>
                          <div data-testid={`kiosk-point-event-${i}`} className="flex-1 k-task rounded-2xl px-3 py-2.5 flex items-center justify-between gap-3 mb-2">
                            <div className="min-w-0">
                              <p className="font-head font-bold text-sm leading-tight">{kt(e.label)}</p>
                              {e.ref && <p className="text-xs k-muted font-semibold truncate">{e.ref}</p>}
                              <p className="text-[11px] k-muted font-semibold mt-0.5">{timeLabel}</p>
                              <p className="text-[11px] font-black mt-0.5" style={{ color: "var(--k-accent)" }}>→ {kt("saldo")} {e.balance}</p>
                            </div>
                            <span className={`font-head font-black shrink-0 ${e.points < 0 ? "text-rose-500" : ""}`} style={e.points >= 0 ? { color: "var(--k-accent)" } : {}}>{e.points > 0 ? "+" : ""}{e.points}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {kioskPostpone && (() => {
        const c = (kioskPostpone.postpone_count || 0) + 1;
        const pen = c === 1 ? 50 : (c === 2 ? 150 : 200);
        return (
          <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-xl flex items-center justify-center p-4" onClick={() => setKioskPostpone(null)} data-testid="kiosk-postpone-modal">
            <div className="k-card rounded-[2rem] p-6 w-full max-w-md" style={theme.vars} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <h3 className="font-head font-black text-2xl leading-tight flex items-center gap-2"><CalendarDays className="w-6 h-6" strokeWidth={2.5} />{kt("Taak uitstellen")}</h3>
                <button data-testid="kiosk-postpone-close" onClick={() => setKioskPostpone(null)} className="k-pill brutal-press rounded-full p-2 shrink-0"><X className="w-5 h-5" strokeWidth={2.5} /></button>
              </div>
              <p className="text-sm k-muted font-semibold mb-3">{kioskPostpone.title}</p>
              <label className="text-sm font-bold">{kt("Nieuwe datum")}</label>
              <input type="date" data-testid="kiosk-postpone-date" value={kpDate} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setKpDate(e.target.value)} className="w-full k-pill rounded-xl px-4 py-3 font-bold outline-none mt-1 mb-3" />
              <label className="text-sm font-bold">{kt("Reden (optioneel)")}</label>
              <input data-testid="kiosk-postpone-reason" value={kpReason} onChange={(e) => setKpReason(e.target.value)} placeholder={kt("Bijv. werk uitgelopen")} className="w-full k-pill rounded-xl px-4 py-3 font-bold outline-none mt-1" />
              {board.rewards_active && (
                <div data-testid="kiosk-postpone-warning" className="mt-3 rounded-xl p-3 text-sm font-bold flex items-center gap-2" style={{ background: "#F5C842", color: "#1a1a1a" }}>
                  <Clock className="w-4 h-4 shrink-0" strokeWidth={3} />
                  <span>{c === 1
                    ? `${kt("Let op: uitstellen kost deze keer")} ${pen} ${kt("punten")}.`
                    : `${kt("Dit is de")} ${c}e ${kt("keer uitstellen — dat kost")} ${pen} ${kt("punten")}.`}</span>
                </div>
              )}
              <button data-testid="kiosk-postpone-confirm" disabled={!kpDate} onClick={() => { const task = kioskPostpone; setKioskPostpone(null); startFlow({ action: "postpone", taskId: task.id, title: task.title, new_date: kpDate, reason: kpReason }); }}
                className="w-full mt-4 k-accent brutal-press rounded-full px-4 py-3 font-head font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50"><CalendarDays className="w-5 h-5" strokeWidth={2.5} />{kt("Uitstellen")}</button>
            </div>
          </div>
        );
      })()}
      {arOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.7)" }} data-testid="kiosk-action-reminder-dialog">
          <div className="k-card w-full max-w-lg p-6" style={theme.vars}>
            <h3 className="font-head font-black text-2xl mb-1 flex items-center gap-2">📣 {kt("Kom in actie sturen")}</h3>
            <p className="text-sm font-semibold opacity-70 mb-3">{kt("Bericht: 'Denk aan jullie taken, dus kom in actie.' via push, e-mail én SMS.")}</p>
            <button data-testid="kiosk-ar-toggle-all" onClick={() => setArAll((v) => !v)} className="w-full flex items-center justify-between py-2 mb-1">
              <span className="font-head font-bold">👪 {kt("Naar iedereen")}</span>
              <span className="w-12 h-7 rounded-full flex items-center px-0.5 transition-colors" style={{ background: arAll ? "var(--k-accent)" : "rgba(0,0,0,0.2)", justifyContent: arAll ? "flex-end" : "flex-start" }}><span className="w-6 h-6 rounded-full bg-white" /></span>
            </button>
            {!arAll && (
              <>
                <button data-testid="kiosk-ar-smart" onClick={() => { const owt = new Set(board?.users_with_open_tasks || []); const ids = (board?.users || []).filter((m) => owt.has(m.user_id) && !(m.role === "super_admin" || m.is_super_admin)).map((m) => m.user_id); if (ids.length === 0) { toast.info(kt("Niemand heeft nog openstaande taken 🎉")); return; } setArSel(ids); }}
                  className="w-full rounded-2xl px-4 py-3 mb-2 font-head font-bold border-2" style={{ borderColor: "var(--k-accent)", color: "var(--k-text)" }}>
                  🎯 {kt("Alleen wie nog openstaande taken heeft")}
                </button>
                <div className="grid grid-cols-2 gap-2 mb-3 max-h-[40vh] overflow-y-auto">
                  {(board?.users || []).filter((m) => !(m.role === "super_admin" || m.is_super_admin)).map((m) => { const on = arSel.includes(m.user_id); return (
                    <button key={m.user_id} data-testid={`kiosk-ar-person-${m.user_id}`} onClick={() => setArSel((s) => on ? s.filter((x) => x !== m.user_id) : [...s, m.user_id])}
                      className="rounded-2xl p-3 font-head font-bold flex items-center gap-2 border-2" style={{ background: on ? "var(--k-accent)" : "transparent", color: on ? "var(--k-accent-ink)" : "var(--k-text)", borderColor: "var(--k-accent)" }}>
                      {on ? <Check className="w-5 h-5 shrink-0" strokeWidth={3} /> : <span className="w-5 h-5 shrink-0" />}<span className="truncate">{m.name}</span>
                    </button>
                  ); })}
                </div>
              </>
            )}
            <div className="flex gap-2 mt-2">
              <button data-testid="kiosk-ar-cancel" onClick={() => setArOpen(false)} className="rounded-2xl px-4 py-3 font-head font-bold border-2" style={{ borderColor: "var(--k-accent)", color: "var(--k-text)" }}>{kt("Annuleren")}</button>
              <button data-testid="kiosk-ar-send" disabled={!arAll && arSel.length === 0} onClick={() => { const uids = arAll ? null : arSel; setArOpen(false); startFlow({ action: "action_reminder", user_ids: uids }); }}
                className="flex-1 rounded-2xl p-3 font-head font-black disabled:opacity-40" style={{ background: "var(--k-text)", color: "var(--k-panel)" }}>{kt("Nu versturen")} 📣</button>
            </div>
          </div>
        </div>
      )}
      {confirmDelete && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.7)" }} data-testid="kiosk-delete-confirm">
          <div className="k-card w-full max-w-sm p-6 text-center" style={theme.vars}>
            <Delete className="w-14 h-14 mx-auto mb-3" strokeWidth={2} style={{ color: "#EF4444" }} />
            <h3 className="font-head font-black text-2xl mb-1">{kt("Weet je het zeker?")}</h3>
            <p className="text-sm font-semibold opacity-70 mb-4">"{confirmDelete.title}" {kt("wordt verwijderd. Dit kan niet ongedaan worden gemaakt.")}</p>
            {confirmDelete.penalty && confirmDelete.penalty.count > 0 && confirmDelete.penalty.points < 0 ? (
              <>
                <p className="text-sm font-bold mb-4" style={{ color: "#EF4444" }}>{kt("Deze taak kreeg")} {Math.abs(confirmDelete.penalty.points)} {kt("strafpunten (te laat). Wat wil je met die punten doen?")}</p>
                <div className="flex flex-col gap-2">
                  <button data-testid="kiosk-delete-revert" onClick={() => { const td = confirmDelete; setConfirmDelete(null); startFlow({ action: "delete", taskId: td.id, title: td.title, revert_penalty: true }); }} className="w-full rounded-full px-4 py-3 font-head font-black text-white" style={{ background: "#EF4444" }}>{kt("Strafpunten terugdraaien & verwijderen")}</button>
                  <button data-testid="kiosk-delete-keep" onClick={() => { const td = confirmDelete; setConfirmDelete(null); startFlow({ action: "delete", taskId: td.id, title: td.title, revert_penalty: false }); }} className="w-full rounded-full px-4 py-3 font-head font-bold border-2" style={{ borderColor: "#EF4444", color: "var(--k-text)" }}>{kt("Strafpunten behouden & verwijderen")}</button>
                  <button data-testid="kiosk-delete-cancel" onClick={() => setConfirmDelete(null)} className="w-full rounded-full px-4 py-2 font-head font-bold" style={{ color: "var(--k-text)" }}>{kt("Annuleren")}</button>
                </div>
              </>
            ) : (
              <div className="flex gap-3">
                <button data-testid="kiosk-delete-cancel" onClick={() => setConfirmDelete(null)} className="flex-1 rounded-full px-4 py-3 font-head font-bold border-2" style={{ borderColor: "var(--k-accent)", color: "var(--k-text)" }}>{kt("Annuleren")}</button>
                <button data-testid="kiosk-delete-proceed" onClick={() => { const td = confirmDelete; setConfirmDelete(null); startFlow({ action: "delete", taskId: td.id, title: td.title }); }} className="flex-1 rounded-full px-4 py-3 font-head font-black text-white" style={{ background: "#EF4444" }}>{kt("Verwijderen")}</button>
              </div>
            )}
          </div>
        </div>
      )}
      {kioskSpin && (
        <WheelOfFortune open prizes={kioskSpin.prizes}
          userId={kioskSpin.user_id || kioskSpin.name}
          subtitle={`${kioskSpin.name} · ${kioskSpin.credits} ${kt("spin(s) te goed")}`}
          onResult={async (prize) => {
            try {
              const { data } = await api.post("/kiosk/spin/spin", { pin: kioskSpin.pin, prize });
              setKioskSpin((s) => (s ? { ...s, credits: data.credits_left } : s));
            } catch (e) { toast.error(e.response?.data?.detail || kt("Draaien mislukt")); }
          }}
          onClose={() => setKioskSpin(null)} />
      )}
      {fsOverlay}
      <Confetti burstKey={burstKey} />
      <NotifPrefsDialog open={prefsOpen} onClose={() => setPrefsOpen(false)} />
    </div>
  );
}
